import React, { useState, useEffect, useMemo, useCallback } from 'react';
// --- Firebase Imports ---
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getFirestore, collection, addDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signInAnonymously } from "firebase/auth";

// --- Firebase Configuration ---
// Your web app's Firebase configuration, now included directly in the code for deployment.
const firebaseConfig = {
  apiKey: "AIzaSyBtkMbifaLbbQDdvTb1WpjG9wuKZVy7V5c",
  authDomain: "crrt-nursing-sim.firebaseapp.com",
  projectId: "crrt-nursing-sim",
  storageBucket: "crrt-nursing-sim.firebasestorage.app",
  messagingSenderId: "337043851442",
  appId: "1:337043851442:web:649f047114aa00628361ab",
  measurementId: "G-P7V3YHW9YQ"
};

// --- Helper Components & Data ---

// Icon components (using inline SVG for self-containment)
const icons = {
  override: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
  history: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  tools: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924-1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  system: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg>,
  lock: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
  help: <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
};
const MAX_BAG_VOLUME = 5000; // mL

// --- Educational Content for Pop-ups ---
const componentInfo = {
  pbp: {
    title: 'Pre-Blood Pump (PBP)',
    description: 'Fluid, either Replacement fluid, or an anticoagulant like citrate, that is infused into the circuit before the blood pump. The rate is measured in mL/h.',
  },
  bfr: {
    title: 'Blood Flow Rate (BFR)',
    description: 'The speed at which blood is pumped from the patient through the CRRT circuit, measured in mL/min. This is a primary driver of clearance.',
  },
  syringe: {
    title: 'Syringe Pump',
    description: 'Used for precise, continuous infusion of medications, most commonly anticoagulants like heparin or citrate, to prevent the circuit from clotting.',
  },
  pfr: {
    title: 'Patient Fluid Removal (PFR)',
    description: 'The rate at which fluid is being removed from the patient, also known as net ultrafiltration. This is a key parameter for managing the patient\'s net fluid balance, measured in mL/h.',
  },
  dialysate: {
    title: 'Dialysate',
    description: 'A solution that runs on the non-blood side of the filter membrane. Waste products are cleared from the blood via diffusion, moving down a concentration gradient into the dialysate.',
  },
  replacement: {
    title: 'Post-Filter Replacement',
    description: 'Sterile replacement fluid infused into the blood circuit after the filter. It replaces volume removed from the patient and contributes to convective clearance.',
  },
  access: {
    title: 'Access Pressure',
    description: 'Measures the negative pressure required to pull blood from the patient\'s access catheter. High negative values can indicate an access problem (e.g., kink, clot, or positional issue). Can also be related to blood flow rate, patient hemodynamics, patient position.',
  },
  return: {
    title: 'Return Pressure',
    description: 'Measures the positive pressure required to push blood back to the patient. High positive values can indicate an obstruction in the return line or catheter. Also affected by blood flow, patient hemodynamics and position, and clotting in deaeration chamber.',
  },
  tmp: {
    title: 'Transmembrane Pressure (TMP)',
    description: 'The pressure difference across the filter membrane (blood side vs. effluent side). Pressure exerted on the filter membrane itself. A gradual rise in TMP indicates the filter is CLOGGING (i.e. protein coating), a sharp rise may indicate a clot in the filter.',
  },
  deltaP: {
    title: 'ΔP (Delta P)',
    description: 'Pressure going through the hollow fibres of the membrane. Delta P = (Filter Pressure - Return Pressure). Gradual rise in Delta P suggests that the filter is starting to CLOT.',
  },
  filter: {
    title: 'Filter Pressure',
    description: 'This display shows the pressure needed to push blood through the filter. It should always be positive. Mainly determined by blood flow, filter size, blood viscosity.',
  },
  effluent: {
    title: 'Effluent Rate',
    description: 'Represents the "dose" of therapy, measured in mL/kg/h. It is the total volume of all fluid removed from the blood, including dialysate, replacement fluids, and net patient fluid removal (ultrafiltration).',
  },
};


// --- Simulation Scenarios ---
// Each scenario declares an alarm `tier`, which determines what the machine does
// on its own when the alarm fires:
//   'warning'  - patient hazard. All pumps stop and the return line clamp closes.
//   'caution'  - fluid pumps stop. Blood and syringe pumps keep running.
//   'advisory' - nothing stops. Therapy continues.
// Workflows therefore no longer contain a manual "stop the blood pump" step.
// Recoverable alarms end with RESUME THERAPY (which restarts whatever stopped);
// unrecoverable ones end with CHANGE SET.
const scenarios = {
  normal: {
    name: "Normal Operation",
    initialState: { pressures: { access: -82, return: 71, tmp: 122, deltaP: 47 }, troubleshooting: null },
  },
  high_tmp_clotting: {
    name: "High TMP Alarm", // Generic name for training
    categoryName: "High TMP",
    tier: "caution",
    initialState: {
      pressures: { access: -95, return: 150, tmp: 280, deltaP: 60 },
      troubleshooting: {
        problem: "High Transmembrane Pressure (TMP).",
        successMessage: "Correct! You lowered the fluid rates and restarted the fluid pumps.",
        learningPoint: "**What the machine did:**\n- Caution: the fluid pumps stopped. The blood pump kept running, so the circuit is still protected while you work.\n\n**Possible Causes:**\n- CLOGGING of filter pores (e.g., protein deposition).\n- Excessive fluid removal (high PFR or Replacement rates).\n- Kinked or clamped effluent (filtrate) line.\n\n**Troubleshooting Steps:**\n- Check all lines for kinks (effluent, access, return).\n- Decrease PFR rate.\n- Decrease Replacement rate.\n- Decrease PBP rate.\n- Increase Blood Flow Rate (BFR).\n- Press RESUME THERAPY to restart the fluid pumps.\n\n**Note:** the blood pump has not stopped and does not need to. Stopping it here interrupts a therapy that is still safe and starts the clock on clotting.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_effluent', text: 'Check the effluent line.', target: 'effluent-line', type: 'component', finding: 'The effluent line is patent and flowing freely, and the effluent is clear. Nothing mechanical is obstructing it.' },
          { id: 'decrease_pfr', text: 'Decrease PFR rate.', target: 'pfr_adjust_down', type: 'action', finding: 'PFR reduced. TMP eases a little but stays high — fluid removal alone is not the whole problem here.', relief: 0.25 },
          { id: 'decrease_rep', text: 'Decrease Replacement rate.', target: 'rep_adjust_down', type: 'action', finding: 'Replacement reduced. Filtration fraction improving.', relief: 0.5 },
          { id: 'decrease_pbp', text: 'Decrease PBP rate.', target: 'pbp_adjust_down', type: 'action', finding: 'PBP reduced.', relief: 0.6 },
          { id: 'increase_bfr', text: 'Increase Blood Flow Rate.', target: 'bfr_adjust_up', type: 'action', finding: 'Blood flow increased — less haemoconcentration inside the filter. TMP is now coming down.', relief: 1.0 },
          { id: 'continue', text: 'Press RESUME THERAPY to restart the fluid pumps.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  high_tmp_rupture: {
    name: "High TMP (Filter Rupture)",
    categoryName: "High TMP",
    tier: "warning",
    initialState: {
      pressures: { access: -95, return: 150, tmp: 280, deltaP: 60 },
      troubleshooting: {
        problem: "High TMP with blood detected in the effluent.",
        successMessage: "Correct! Blood was visible in the effluent, so the circuit must be changed. The blood is not returned.",
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed. The blood leak detector triggers this automatically.\n\n**Possible Causes:**\n- CRITICAL: Rupture of the filter membrane.\n- Severe hemolysis (rare).\n- False positive from discolored fluid or a dirty sensor.\n\n**Troubleshooting Steps:**\n- The pump has already stopped. Visually inspect the effluent line for blood.\n- If blood is present, press CHANGE SET. The entire circuit must be replaced and the blood is NOT returned.\n- If the effluent is amber-brown rather than pink, consider bilirubin or myoglobin and send a sample.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_effluent', text: 'Check the effluent line.', target: 'effluent-line', type: 'component', finding: 'The effluent is PINK. Frank blood is visible in the line and collecting in the bag. The membrane has ruptured.' },
          { id: 'change_set', text: 'Blood is visible. Press CHANGE SET.', target: 'key_change_set', type: 'softkey' },
        ]
      }
    }
  },
  high_tmp_excessive_pfr: {
    name: "High TMP (Excessive PFR)",
    tier: "caution",
    initialState: {
      pressures: { access: -95, return: 150, tmp: 280, deltaP: 60 },
      troubleshooting: {
        problem: "High Transmembrane Pressure (TMP).",
        successMessage: "Correct! The PFR rate was too high, causing hemoconcentration. Lowering it resolved the alarm.",
        learningPoint: "**What the machine did:**\n- Caution: the fluid pumps stopped. The blood pump kept running.\n\nAn excessive PFR rate raises the filtration fraction, haemoconcentrates blood inside the filter and clogs the membrane. Lowering the rate was the correct solution. Press RESUME THERAPY to restart the fluid pumps once it is set.\n\nAim to keep the filtration fraction below about 25%. Moving replacement fluid pre-filter lowers it without losing dose.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_effluent', text: 'Check the effluent line.', target: 'effluent-line', type: 'component', finding: 'The effluent line is patent and flowing freely, and the effluent is clear. Nothing mechanical is obstructing it.' },
          { id: 'decrease_pfr', text: 'Decrease PFR rate.', target: 'pfr_adjust_down', type: 'action', finding: 'PFR reduced. TMP falls sharply and the filtration fraction drops back below 25%. This was the cause.', relief: 1.0 },
          { id: 'continue', text: 'Press RESUME THERAPY to restart the fluid pumps.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  high_tmp_kinked_effluent: {
    name: "High TMP (Kinked Effluent Line)",
    tier: "caution",
    initialState: {
      pressures: { access: -95, return: 150, tmp: 280, deltaP: 60 },
      troubleshooting: {
        problem: "High Transmembrane Pressure (TMP).",
        successMessage: "Correct! The effluent line was kinked. Relieving it and restarting the fluid pumps fixed the pressure.",
        learningPoint: "**What the machine did:**\n- Caution: the fluid pumps stopped. The blood pump kept running.\n\nTMP is calculated from the filter, return and effluent pressures, so a kink or clamp on the effluent line drives it up just as hard as excessive fluid removal does. Always look at the line before changing the prescription.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_effluent', text: 'Check the effluent line.', target: 'effluent-line', type: 'component', finding: 'The effluent line is KINKED where it leaves the pump. Kink straightened — TMP falls immediately. This was the cause.', relief: 1.0 },
          { id: 'continue', text: 'Press RESUME THERAPY to restart the fluid pumps.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  delta_p_high_kink: {
    name: "Membrane Pressure rising (Kink)",
    categoryName: "Membrane Pressure rising (Delta P)",
    tier: "advisory",
    initialState: {
      pressures: { access: -120, return: 180, tmp: 150, deltaP: 250 },
      troubleshooting: {
        problem: "Membrane Pressure rising (High Delta P).",
        successMessage: "Correct! You found the obstruction and cleared the advisory.",
        learningPoint: "**What the machine did:**\n- Advisory: nothing stopped. Therapy continues while you act, and this is your warning before the filter fails outright.\n\n**Possible Causes:**\n- Clamped or kinked line.\n- Inadequate anticoagulation.\n- Air leak at the filter or return pressure sensors.\n- Excessive flow rates.\n- CLOT formation in the filter.\n\n**Troubleshooting Steps:**\n- Check the access and return lines for kinks or clamps.\n- Check the filter for visible clots.\n- Press RESUME THERAPY once the cause is corrected.\n\n**Note:** stopping the blood pump here accelerates the very clotting the alarm is warning about.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_return', text: 'Check the return and access lines for kinks.', target: 'return-line', type: 'component', finding: 'A KINK is found in the return line. Straightened — Delta P falls immediately. This was the cause.', relief: 1.0 },
          { id: 'override', text: 'Press RESUME THERAPY to clear the advisory.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  delta_p_high_clotting: {
    name: "Membrane Pressure rising (Delta P)", // Generic name for training
    categoryName: "Membrane Pressure rising (Delta P)",
    tier: "advisory",
    initialState: {
      pressures: { access: -120, return: 180, tmp: 150, deltaP: 250 },
      troubleshooting: {
        problem: "Membrane Pressure rising (High Delta P).",
        successMessage: "Correct! You addressed the anticoagulation and flow rates driving the filter to clot.",
        learningPoint: "**What the machine did:**\n- Advisory: nothing stopped. Therapy continues while you act.\n\nDelta P is filter pressure minus return pressure, so it rises as the hollow fibres start to occlude. It climbs before the filter clots completely, which makes this the alarm most worth acting on early.\n\n**Possible Causes:**\n- Inadequate anticoagulation — the leading cause of filter clotting.\n- Excessive ultrafiltration (high filtration fraction).\n- Blood flow rate too low for the filter.\n- Air leak at the filter or return pressure sensors.\n\n**Troubleshooting Steps:**\n- Check the filter for visible clots.\n- Start or increase the anticoagulant infusion.\n- Decrease fluid removal (PFR).\n- INCREASE the blood flow rate — clotting is delayed by adequate anticoagulation, lower ultrafiltration rates and higher blood rates.\n- Press RESUME THERAPY to clear the advisory.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_return', text: 'Check the return and access lines for kinks.', target: 'return-line', type: 'component', finding: 'Both lines are patent with no kink or clamp. The resistance is inside the filter, not in the tubing.' },
          { id: 'check_filter', text: 'Check the filter for clots.', target: 'filter', type: 'component', finding: 'The filter fibres look dark and striped. They are starting to clot, though the membrane is still working.' },
          { id: 'increase_anticoag', text: 'Start or increase the anticoagulant infusion.', target: 'syringe_adjust_up', type: 'action', finding: 'Anticoagulant infusion started. Post-filter levels will need rechecking.', relief: 0.3 },
          { id: 'decrease_pfr', text: 'Decrease Fluid Removal (PFR).', target: 'pfr_adjust_down', type: 'action', finding: 'PFR reduced. Filtration fraction falling.', relief: 0.55 },
          { id: 'increase_bfr', text: 'Increase Blood Flow (BFR).', target: 'bfr_adjust_up', type: 'action', finding: 'Blood flow increased — less dwell time in the fibres. Delta P is stabilising.', relief: 1.0 },
          { id: 'override', text: 'Press RESUME THERAPY to clear the advisory.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  filter_pressure_excessive: {
    name: "Filter Pressure Excessive",
    categoryName: "Filter pressure extremely positive",
    tier: "warning",
    initialState: {
      pressures: { access: -150, return: 95, tmp: 317, deltaP: 325 },
      troubleshooting: {
        problem: "Filter Pressure Excessive — filter clotted.",
        successMessage: "Correct! The filter was clotted and the circuit must be replaced.",
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed when filter pressure hit its hard limit.\n\n**Possible Causes:**\n- This is a critical indicator of CLOTTING in the filter.\n- Inadequate anticoagulation is the leading cause.\n- A prolonged blood pump stop (e.g. a slow bag change) will clot a circuit abruptly with no prior advisory.\n\n**Troubleshooting Steps:**\n- The pump has already stopped. Inspect the filter.\n- Press CHANGE SET. A grossly clotted circuit often cannot be returned, so assess before attempting it.\n- Ask whether a rising Delta P advisory preceded this. If it did, the anticoagulation strategy needs reviewing before the next set is run.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_filter', text: 'Inspect the filter.', target: 'filter', type: 'component', finding: 'The filter is dark throughout with gross clotting. This circuit cannot be salvaged.' },
          { id: 'change_set', text: 'Press CHANGE SET to replace the circuit.', target: 'key_change_set', type: 'softkey' }
        ]
      }
    }
  },
  foam_in_chamber_connections: {
    name: "Foam in Chamber Alarm", // Generic name for training
    categoryName: "Foam in De-aeration Chamber",
    tier: "warning",
    initialState: {
      pressures: { access: -82, return: 71, tmp: 122, deltaP: 47 },
      troubleshooting: {
        problem: "Foam detected in the de-aeration chamber.",
        successMessage: "Correct! You found and tightened the loose connection on the access line, then restarted the pumps.",
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed. Foam at the chamber trips the air detector.\n\n**Possible Causes:**\n- Loose connections, especially on the pre-pump (access) side, which sits under negative pressure and draws air IN rather than leaking blood out.\n- Improperly primed circuit or bags running dry.\n- Low blood level in the de-aeration chamber (bubble trap).\n- Crack in a line or the filter housing.\n\n**Troubleshooting Steps:**\n- The pump has already stopped. Inspect the de-aeration chamber and its level.\n- Inspect all circuit connections to ensure they are tight.\n- Press RESUME THERAPY once the source is corrected.\n\n**Prevention:** run 200–500 mL/h of post-filter replacement into the chamber to prevent an air/blood interface.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'inspect_chamber', text: 'Inspect the de-aeration chamber.', target: 'deaeration_chamber', type: 'component', finding: 'Foam sits above the blood, and the level is below the marked line.' },
          { id: 'check_access', text: 'Check all pre-pump connections.', target: 'access-line', type: 'component', finding: 'The access luer before the blood pump is LOOSE. Tightened — this is where air was being drawn in.' },
          { id: 'check_return', text: 'Check all post-pump connections.', target: 'return-line', type: 'component', finding: 'Post-pump connections are all secure.' },
          { id: 'continue', text: 'Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  foam_in_chamber_crack: {
    name: "Foam in Chamber (Line Crack)",
    categoryName: "Foam in De-aeration Chamber",
    tier: "warning",
    initialState: {
      pressures: { access: -82, return: 71, tmp: 122, deltaP: 47 },
      troubleshooting: {
        problem: "Foam detected in the de-aeration chamber.",
        successMessage: "Correct. With every connection secure, a crack in the line or filter is the remaining cause. The circuit must be replaced.",
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed.\n\n**Possible Causes:**\n- Loose connections, especially on the pre-pump (access) side.\n- Improperly primed circuit or bags running dry.\n- Low blood level in the de-aeration chamber.\n- Crack in a line or the filter housing.\n\n**Troubleshooting Steps:**\n- The pump has already stopped. Inspect the chamber and every connection first.\n- If all connections are secure and foam persists, a crack in the line or housing is the remaining cause. Press CHANGE SET.\n- If air or froth has passed the detector, do not return the blood.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'inspect_chamber', text: 'Inspect the de-aeration chamber.', target: 'deaeration_chamber', type: 'component', finding: 'Dense foam throughout the chamber.' },
          { id: 'check_access', text: 'Check pre-pump connections. All are secure.', target: 'access-line', type: 'component', finding: 'All pre-pump connections are secure and tight.' },
          { id: 'check_return', text: 'Check post-pump connections. All are secure.', target: 'return-line', type: 'component', finding: 'All post-pump connections are secure. With everything tight, a crack in the line or housing is the only remaining cause.' },
          { id: 'change_set', text: 'A crack is the remaining cause. Press CHANGE SET.', target: 'key_change_set', type: 'softkey' },
        ]
      }
    }
  },
  air_detection_alarm: {
    name: "Air Detection Alarm",
    categoryName: "Circuit Alarms",
    tier: "warning",
    initialState: {
      pressures: { access: -82, return: 71, tmp: 122, deltaP: 47 },
      troubleshooting: {
        problem: "Air detected in the return line.",
        successMessage: "Correct! You checked every air source, corrected the chamber level and restarted the pumps.",
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped AND the return line clamp closed. This is the anti-embolism interlock and it is the single most important safety behaviour on the machine.\n\n**Possible Causes:**\n- Low blood level in the de-aeration chamber (bubble trap).\n- Loose connections anywhere in the circuit, especially pre-pump.\n- Improperly primed circuit.\n- The chamber has dislodged from its holder, or the detector is faulty.\n\n**Troubleshooting Steps:**\n- The pump has already stopped and the return line is clamped. Do not attempt to open the clamp until the air is cleared.\n- Inspect the de-aeration chamber level and reseat it if it has dislodged.\n- Check all connections, the filter housing and the effluent line.\n- Press RESUME THERAPY once the return line is confirmed free of air.\n- If the circuit is frothy or air has passed the detector, it must be replaced and the blood NOT returned.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'inspect_chamber', text: 'Inspect the de-aeration chamber level.', target: 'deaeration_chamber', type: 'component', finding: 'The blood level has fallen well below the marked line, leaving an air/blood interface. Level restored.' },
          { id: 'check_access', text: 'Check access line connections.', target: 'access-line', type: 'component', finding: 'Access line connections are secure.' },
          { id: 'check_return', text: 'Check return line connections.', target: 'return-line', type: 'component', finding: 'Return line traced. Now clear of air below the detector.' },
          { id: 'check_filter', text: 'Check filter housing for cracks.', target: 'filter', type: 'component', finding: 'Filter housing is intact. No cracks.' },
          { id: 'continue', text: 'Return line is clear. Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  access_occlusion_kink: {
      name: "High Negative Access Pressure (Kink)",
      categoryName: "High Negative Access Pressure",
      tier: "warning",
      initialState: {
          pressures: { access: -220, return: 65, tmp: 90, deltaP: 210 },
          troubleshooting: {
              problem: "High negative Access pressure.",
              successMessage: "Correct! You identified and resolved the kink in the access line, then restarted the pumps.",
              learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed when access pressure passed its limit.\n\n**Possible Causes:**\n- Access catheter is kinked or clamped.\n- Catheter is malpositioned (against the vessel wall).\n- Access catheter is clotted.\n- Patient is hypovolemic or has low blood flow.\n- BFR is set too high for the catheter.\n\n**Troubleshooting Steps:**\n- Work outside in: rule out what you can see before treating what you cannot.\n- Check the access line for visible kinks or clamps.\n- Press RESUME THERAPY once the kink is relieved.\n\n**Prevention:** match catheter size to the prescribed blood flow. A small catheter with a high flow rate is the commonest driver of this alarm.",
              workflow: [
                  { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
                  { id: 'check_line', text: 'Trace the access line from patient to machine.', target: 'access-line', type: 'component', finding: 'A KINK is found where the access line crosses the bed rail. Straightened — access pressure improving. This was the cause.', relief: 1.0 },
                  { id: 'continue', text: 'Kink relieved. Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' }
              ]
          }
      }
  },
  access_occlusion_clot_positional: {
      name: "High Negative Access Pressure", // Generic name for training
      categoryName: "High Negative Access Pressure",
      tier: "warning",
      initialState: {
          pressures: { access: -220, return: 65, tmp: 90, deltaP: 210 },
          troubleshooting: {
              problem: "High negative Access pressure.",
              successMessage: "Correct! With no kink and a lumen that will not aspirate, reversing the lines resolved the pressure.",
              learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed.\n\n**Possible Causes:**\n- Access catheter is kinked or clamped.\n- Catheter is malpositioned (against the vessel wall).\n- Access catheter is clotted.\n- Patient is hypovolemic or has low blood flow.\n- BFR is set too high.\n\n**Troubleshooting Steps:**\n- Check the access line for kinks or clamps, then aspirate the lumen for a clot.\n- Try reducing the Blood Flow Rate (BFR).\n- As a final step, reverse the access and return lines.\n- Press RESUME THERAPY to restart the pumps.\n\n**Caution:** reversing the lines is a holding measure. It causes significant recirculation and reduced clearance, and the catheter should be assessed for replacement.",
              workflow: [
                  { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
                  { id: 'check_line', text: 'Check the access line and aspirate for a clot.', target: 'access-line', type: 'component', finding: 'No kink or clamp. The access lumen meets marked RESISTANCE and will not aspirate — a clot is likely.' },
                  { id: 'lower_bfr', text: 'Lower the Blood Flow Rate (BFR).', target: 'bfr_adjust_down', type: 'action', finding: 'Blood flow reduced, but access pressure stays highly negative. The problem is not the rate.', relief: 0.2 },
                  { id: 'reverse_lines', text: 'Reverse access/return lines.', target: 'reverse_lines_button', type: 'action', finding: 'Lines reversed. Access pressure settles. Note this causes recirculation and reduced clearance — the catheter should be assessed for replacement.', relief: 1.0 },
                  { id: 'continue', text: 'Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' }
              ]
          }
      }
  },
  access_occlusion_excessive_bfr: {
      name: "High Negative Access Pressure (Excessive BFR)",
      tier: "warning",
      initialState: {
          pressures: { access: -220, return: 65, tmp: 90, deltaP: 210 },
          troubleshooting: {
              problem: "High negative Access pressure.",
              successMessage: "Correct! The BFR was too high for the catheter. Lowering it resolved the alarm.",
              learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed.\n\nThe line was patent and the lumen aspirated freely, which rules out the mechanical causes and leaves the prescription. A blood flow rate set above what the catheter can deliver produces exactly this picture.\n\n**Troubleshooting Steps:**\n- Check the access line — no kink found.\n- Lower the Blood Flow Rate.\n- Press RESUME THERAPY to restart the pumps.\n\n**Note:** hypovolemia presents the same way — the lumen aspirates freely and the pressure improves when flow is reduced — so check the haemodynamics before assuming the catheter is at fault.",
              workflow: [
                  { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
                  { id: 'check_line', text: 'Trace the access line. No kinks found.', target: 'access-line', type: 'component', finding: 'No kink or clamp. The lumen ASPIRATES FREELY with no clot — the catheter is patent.' },
                  { id: 'lower_bfr', text: 'Lower the Blood Flow Rate (BFR).', target: 'bfr_adjust_down', type: 'action', finding: 'Blood flow reduced and access pressure improves immediately. The rate was above what this catheter can deliver.', relief: 1.0 },
                  { id: 'continue', text: 'Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' }
              ]
          }
      }
  },
  return_occlusion_kink: {
      name: "High Return Pressure (Kink)",
      categoryName: "High Return Pressure",
      tier: "warning",
      initialState: {
          pressures: { access: -70, return: 250, tmp: 240, deltaP: 55 },
          troubleshooting: {
              problem: "High Return pressure.",
              successMessage: "Correct! You identified the kink in the return line and restarted the pumps.",
              learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed when return pressure passed its limit.\n\n**Possible Causes:**\n- Return line or catheter is kinked or clamped.\n- Return catheter is clotted.\n- Catheter is dislodged or has infiltrated (causing a hematoma).\n\n**Troubleshooting Steps:**\n- Trace the return line from the machine to the patient, checking for kinks or clamps — including under the dressing at the catheter hub.\n- Press RESUME THERAPY once the kink is relieved.",
              workflow: [
                  { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
                  { id: 'check_line', text: 'Trace the return line from machine to patient.', target: 'return-line', type: 'component', finding: 'A KINK is found at the catheter hub, under the dressing. Straightened — return pressure falling. This was the cause.', relief: 1.0 },
                  { id: 'continue', text: 'Kink relieved. Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' }
              ]
          }
      }
  },
  return_occlusion_clot: {
      name: "High Return Pressure", // Generic name for training
      categoryName: "High Return Pressure",
      tier: "warning",
      initialState: {
          pressures: { access: -70, return: 250, tmp: 240, deltaP: 55 },
          troubleshooting: {
              problem: "High Return pressure.",
              successMessage: "Correct! With no kink and a return lumen that will not clear, the catheter must be resited and the set changed.",
              learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed.\n\n**Possible Causes:**\n- Return line or catheter is kinked or clamped.\n- Return catheter is clotted.\n- Catheter is dislodged or has infiltrated (causing a hematoma).\n\n**Troubleshooting Steps:**\n- Trace the return line for kinks or clamps.\n- Inspect the catheter exit site. Swelling or a catheter that has migrated from its marked depth points to infiltration, not a clot — that needs resiting rather than flushing.\n- Aspirate the RETURN lumen. A return pressure alarm means the obstruction is on the return side, so aspirating the access lumen tells you nothing about it.\n- If the lumen will not clear, the catheter must be resited. Press CHANGE SET.",
              workflow: [
                  { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
                  { id: 'check_line', text: 'Trace the return line. No kinks found.', target: 'return-line', type: 'component', finding: 'The return line is patent along its length, no kink or clamp. The exit site is clean with no swelling, so it has not infiltrated.' },
                  { id: 'aspirate_return', text: 'Aspirate the RETURN lumen for a clot. It will not clear.', target: 'return-line', type: 'component', finding: 'The return lumen meets RESISTANCE and will not aspirate or flush. The catheter is clotted and must be resited.' },
                  { id: 'change_set', text: 'The catheter must be resited. Press CHANGE SET.', target: 'key_change_set', type: 'softkey' },
              ]
          }
      }
  },
  empty_dialysate: {
    name: "Empty Dialysate Bag",
    tier: "caution",
    initialState: {
      pressures: { access: -82, return: 71, tmp: 122, deltaP: 47 },
      troubleshooting: {
          problem: "Dialysate bag is empty. Fluid delivery stopped.",
          successMessage: "Excellent. Bag changed and the fluid pumps restarted.",
           workflow: [
               { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
               { id: 'change_bag', text: 'Use the Fluid Manager to change the dialysate bag.', target: 'change_dia_bag', type: 'action', finding: 'New dialysate bag hung and spiked. Remember the scale must be physically opened and closed for the machine to reweigh it.' },
               { id: 'continue', text: 'Press RESUME THERAPY to restart the fluid pumps.', target: 'key_continue', type: 'softkey' }
            ],
           learningPoint: "**What the machine did:**\n- Caution: the fluid pumps stopped. The blood and syringe pumps kept running, so the circuit is not at risk while you change the bag.\n\n**Possible Causes:**\n- The fluid bag has run empty based on the prescribed rate.\n- The fluid line is kinked or clamped.\n- The frangible pin is unbroken or the bag is not fully spiked.\n\n**Troubleshooting Steps:**\n- Acknowledge the alarm.\n- Check the fluid line for kinks or clamps, and confirm the pin is broken — a weight alarm does not always mean an empty bag.\n- Use the Fluid Manager to replace the bag.\n- Press RESUME THERAPY to restart the fluid pumps.\n\n**At the bedside:** the scale must be physically opened and closed for the machine to reweigh. Hanging a bag without opening the scale causes weight-incorrect alarms and compromises fluid balance."
      }
    }
  },
  empty_pbp_bag: {
    name: "Empty PBP Bag",
    tier: "caution",
    initialState: {
      pressures: { access: -82, return: 71, tmp: 122, deltaP: 47 },
      troubleshooting: {
          problem: "PBP bag is empty. Fluid delivery stopped.",
          successMessage: "Excellent. Bag changed and the fluid pumps restarted.",
           workflow: [
               { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
               { id: 'change_bag', text: 'Use the Fluid Manager to change the PBP bag.', target: 'change_pbp_bag', type: 'action', finding: 'New PBP bag hung and spiked. Remember the scale must be physically opened and closed for the machine to reweigh it.' },
               { id: 'continue', text: 'Press RESUME THERAPY to restart the fluid pumps.', target: 'key_continue', type: 'softkey' }
            ],
           learningPoint: "**What the machine did:**\n- Caution: the fluid pumps stopped. The blood and syringe pumps kept running.\n\nPBP runs before the blood pump, so when it stops the predilution — and often the citrate — stops with it. Change it promptly: a circuit running without its anticoagulant is a circuit heading for a clot.\n\n**Possible Causes:**\n- The bag has run empty at the prescribed rate.\n- The line is kinked or clamped.\n- The frangible pin is unbroken or the bag is not fully spiked.\n\n**Troubleshooting Steps:**\n- Check the line and the pin before assuming the bag is empty.\n- Use the Fluid Manager to replace the bag, then press RESUME THERAPY."
      }
    }
  },
  empty_replacement_bag: {
    name: "Empty Replacement Bag",
    tier: "caution",
    initialState: {
      pressures: { access: -82, return: 71, tmp: 122, deltaP: 47 },
      troubleshooting: {
          problem: "Replacement bag is empty. Fluid delivery stopped.",
          successMessage: "Excellent. Bag changed and the fluid pumps restarted.",
           workflow: [
               { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
               { id: 'change_bag', text: 'Use the Fluid Manager to change the Replacement bag.', target: 'change_rep_bag', type: 'action', finding: 'New replacement bag hung and spiked. Remember the scale must be physically opened and closed for the machine to reweigh it.' },
               { id: 'continue', text: 'Press RESUME THERAPY to restart the fluid pumps.', target: 'key_continue', type: 'softkey' }
            ],
           learningPoint: "**What the machine did:**\n- Caution: the fluid pumps stopped. The blood and syringe pumps kept running.\n\nPost-filter replacement running into the de-aeration chamber is what keeps an air/blood interface from forming. When it stops, foaming and air alarms follow — so this one is worth changing promptly.\n\n**Possible Causes:**\n- The bag has run empty at the prescribed rate.\n- The line is kinked or clamped.\n- The spike is not fully seated.\n\n**Troubleshooting Steps:**\n- Check the line and the spike before assuming the bag is empty.\n- Use the Fluid Manager to replace the bag, then press RESUME THERAPY."
      }
    }
  },
  blood_leak: {
    name: "Blood Leak Alarm",
    tier: "warning",
    initialState: {
      pressures: { access: -85, return: 75, tmp: 110, deltaP: 50 },
      troubleshooting: {
        problem: "CRITICAL: Blood Leak Detected.",
        successMessage: "Correct! Blood was visible in the effluent. The entire circuit must be changed and the blood is not returned.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the CRITICAL alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_effluent', text: 'Check the effluent line for blood.', target: 'effluent-line', type: 'component', finding: 'The effluent is PINK with frank blood visible. The membrane has ruptured. Do not return the blood.' },
          { id: 'change_set', text: 'Blood is visible. Press CHANGE SET.', target: 'key_change_set', type: 'softkey' },
        ],
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed. The blood leak detector triggers this automatically — you do not stop the pump yourself.\n\n**Possible Causes:**\n- CRITICAL: the filter membrane has ruptured.\n- Severe hemolysis (rare).\n- False positive from discolored effluent or a dirty sensor.\n\n**Troubleshooting Steps:**\n- The pump has already stopped. Visually inspect the effluent line and bag.\n- Frank blood or pink effluent means the membrane has gone: press CHANGE SET and do NOT return the blood.\n- Amber-brown effluent with no frank blood can be bilirubin or myoglobin from the patient's disease state. Send a sample if in doubt.\n- If the effluent line is handled for any reason, the blood leak detector must be normalized before treatment continues — and the line verified free of blood at that moment."
      }
    }
  },
  circuit_disconnect: {
    name: "Low Return Pressure",
    categoryName: "Pressure Alarms",
    tier: "warning",
    initialState: {
      pressures: { access: -20, return: 5, tmp: 25, deltaP: 20 },
      troubleshooting: {
        problem: "CRITICAL: Return pressure below the detection window.",
        successMessage: "Correct! The line was connected, so the blood flow rate was raised into the monitoring window and the pumps restarted.",
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed. This is the disconnection interlock.\n\n**Possible Causes:**\n- CRITICAL: the return line has disconnected from the patient.\n- Blood Flow Rate is set too low for the machine to sense the connection.\n- A clamp is present on the line before the pressure sensor.\n\n**Troubleshooting Steps:**\n- The pump has already stopped. Lay eyes on the return line at the patient BEFORE anything else.\n- If it is disconnected, clamp the line, control the bleeding, and change the set. The blood cannot be returned.\n- If it is connected, the pressure is simply below the window the machine can read. Raise the blood flow rate, then press RESUME THERAPY.\n\n**Critical point:** the machine needs return pressure at or above +10 mmHg and access pressure at or below −10 mmHg to confirm a connection. Never raise the blood flow rate to clear this alarm before confirming the line is attached — if it is disconnected, increasing flow pumps the patient's blood onto the floor.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the CRITICAL alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_line', text: 'Inspect the return line connection at the patient. It is secure.', target: 'return-line', type: 'component', finding: 'The return line is CONNECTED and secure at the catheter hub. Return pressure is only +5 mmHg — below the +10 the machine needs to sense a connection.' },
          { id: 'increase_bfr', text: 'Raise the Blood Flow Rate into the monitoring window.', target: 'bfr_adjust_up', type: 'action', finding: 'Blood flow increased. Return pressure rises back into the monitoring window.', relief: 1.0 },
          { id: 'continue', text: 'Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
  disconnection_alarm: {
    name: "Disconnection Alarm",
    categoryName: "Circuit Alarms",
    tier: "warning",
    initialState: {
      pressures: { access: 5, return: 15, tmp: 25, deltaP: 20 },
      troubleshooting: {
        problem: "Disconnection Alarm (Positive Access Pressure).",
        successMessage: "Correct! Every connection was secure, so the blood flow rate was raised into the monitoring window and the pumps restarted.",
        learningPoint: "**What the machine did:**\n- Warning: all pumps stopped and the return line clamp closed.\n\n**Possible Causes:**\n- A line has disconnected, most likely on the access side before the pump.\n- A line is kinked or clamped BEFORE the access pressure sensor.\n- Blood Flow Rate is set too low for the machine to sense the connection.\n\n**Troubleshooting Steps:**\n- The pump has already stopped. Check the circuit and the patient for any disconnection.\n- Check the de-aeration chamber for entrained air. Everything before the blood pump sits under negative pressure, so a loose connection there draws air IN rather than leaking blood out.\n- If air has been drawn past the pump, the set is finished — change it.\n- If every connection is secure and no air is present, raise the blood flow rate and press RESUME THERAPY.",
        workflow: [
          { id: 'acknowledge', text: 'Acknowledge the alarm.', target: 'acknowledge_button', type: 'button' },
          { id: 'check_access', text: 'Check access line connections.', target: 'access-line', type: 'component', finding: 'All access connections are secure and fully coupled.' },
          { id: 'check_chamber', text: 'Check the de-aeration chamber for entrained air. None found.', target: 'deaeration_chamber', type: 'component', finding: 'Chamber level is correct. No air has been drawn past the pump.' },
          { id: 'check_return', text: 'Check return line connections.', target: 'return-line', type: 'component', finding: 'Return line connections are secure.' },
          { id: 'increase_bfr', text: 'Raise the Blood Flow Rate into the monitoring window.', target: 'bfr_adjust_up', type: 'action', finding: 'Blood flow increased. Access pressure falls back below −10 mmHg and the machine can sense the connection again.', relief: 1.0 },
          { id: 'continue', text: 'Press RESUME THERAPY to restart the pumps.', target: 'key_continue', type: 'softkey' },
        ]
      }
    }
  },
};

// --- ALARM CATEGORIES ---
// This now matches your requested grouping.
const alarmGroupsConfig = {
    pressure: {
        name: "Pressure Alarms",
        keys: ['access_occlusion', 'return_occlusion', 'circuit_disconnect'] // circuit_disconnect (Low Return) moved here
    },
    filter: {
        name: "Filter Pressure Alarms",
        keys: ['delta_p_high', 'high_tmp', 'filter_pressure_excessive'] // Added new scenario
    },
    circuit: {
        name: "Circuit Alarms",
        keys: ['foam_in_chamber', 'blood_leak', 'air_detection_alarm', 'disconnection_alarm'] // Added new alarms
    },
    nonUrgent: {
        name: "Non-Urgent Alarms",
        keys: ['empty_dialysate', 'empty_pbp_bag', 'empty_replacement_bag']
    }
};

// This maps the *category* keys to their display names and sub-scenarios
// This is used by Practice Mode to get random scenarios and by Training Mode to show the right buttons
const scenarioCategories = {
    high_tmp: { name: "High TMP", keys: ['high_tmp_clotting', 'high_tmp_rupture']},
    delta_p_high: { name: "Membrane Pressure rising (Delta P)", keys: ['delta_p_high_clotting', 'delta_p_high_kink']},
    foam_in_chamber: { name: "Foam in Chamber", keys: ['foam_in_chamber_connections', 'foam_in_chamber_crack']},
    access_occlusion: { name: "High Negative Access Pressure", keys: ['access_occlusion_clot_positional', 'access_occlusion_kink']},
    return_occlusion: { name: "High Return Pressure", keys: ['return_occlusion_clot', 'return_occlusion_kink']},
};

// --- Main UI Components ---

const Header = ({ therapyMode, onModeClick, onGiveFeedback, mode, onModeToggle }) => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <header className="flex justify-between items-center p-4 bg-slate-900/80 text-white border-b border-slate-700/50 shadow-lg backdrop-blur-sm">
      <div className="flex items-center space-x-4">
        <h1 className="text-3xl font-bold text-sky-300 tracking-wide">Therapy</h1>
        <button onClick={onModeClick} className="text-lg bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 shadow-md">{therapyMode}</button>
      </div>

       <div className="flex items-center space-x-2 bg-slate-800 p-1 rounded-full shadow-inner">
          <button 
            onClick={() => onModeToggle('training')}
            className={`px-5 py-2 rounded-full font-semibold text-lg transition-all duration-300 ${mode === 'training' ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg scale-105' : 'text-gray-400 hover:bg-slate-700'}`}
          >
            Review
          </button>
          <button 
            onClick={() => onModeToggle('practice')}
            className={`px-5 py-2 rounded-full font-semibold text-lg transition-all duration-300 ${mode === 'practice' ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg scale-105' : 'text-gray-400 hover:bg-slate-700'}`}
          >
            Practice
          </button>
      </div>

      <div className="flex items-center space-x-6">
        <div className="text-right">
          <p className="text-base text-slate-400">{time.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p className="text-2xl font-mono text-slate-200 tracking-widest">{time.toLocaleTimeString()}</p>
        </div>
        <button onClick={onGiveFeedback} className="bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 text-white font-bold text-xl px-6 py-3 rounded-lg shadow-lg hover:shadow-purple-500/30 transition-all duration-200 hover:scale-105">
            Give Feedback
        </button>
      </div>
    </header>
  );
};

const PressureGauge = ({ label, value, min, max, unit, onClick }) => {
  const percentage = useMemo(() => {
    const range = max - min;
    const valueInRange = Math.max(min, Math.min(max, value)) - min;
    return (valueInRange / range) * 100;
  }, [value, min, max]);

  const barColor = useMemo(() => {
    if (label === 'Access') {
        if (value < -200) return 'bg-gradient-to-t from-red-500 to-red-700';
        if (value < -150) return 'bg-gradient-to-t from-amber-400 to-amber-600';
    } else {
        const p = (value - min) / (max - min);
        if (p > 0.85) return 'bg-gradient-to-t from-red-500 to-red-700';
        if (p > 0.65) return 'bg-gradient-to-t from-amber-400 to-amber-600';
    }
    return 'bg-gradient-to-t from-emerald-400 to-emerald-600';
  }, [value, min, max, label]);

  return (
    <div className="flex flex-col items-center w-24 p-3 bg-slate-800/70 rounded-xl cursor-pointer shadow-lg hover:shadow-slate-700/50 transition-all duration-200 hover:scale-105 border border-slate-700/50" onClick={onClick}>
      <span className="text-base font-semibold text-slate-300">{label}</span>
      <div className="w-12 h-48 bg-slate-900 rounded-lg mt-2 flex flex-col-reverse overflow-hidden border-2 border-slate-600 shadow-inner">
        <div className={`w-full ${barColor} transition-all duration-500`} style={{ height: `${percentage}%` }}></div>
      </div>
      <span className="text-3xl font-bold mt-3 text-white font-mono">{value}</span>
      <span className="text-sm text-slate-400">{unit}</span>
    </div>
  );
};

const ParameterDisplay = ({ label, value, unit, color, onAdjust, bagType, bagVolume, showButtons = true, onClick, currentTargetId, paramKey }) => {
    const gradientClasses = {
        red: 'from-red-500 to-red-700',
        pink: 'from-pink-500 to-pink-600',
        blue: 'from-sky-500 to-sky-700',
        cyan: 'from-cyan-400 to-cyan-600',
        green: 'from-emerald-500 to-emerald-700',
        purple: 'from-purple-500 to-purple-700',
    };

    const bgColorMap = {
        pink: 'bg-pink-600',
        green: 'bg-emerald-600',
        purple: 'bg-purple-600',
    };

    const borderColor = `border-${color}-400/50`;
    const gradient = gradientClasses[color] || 'from-gray-500 to-gray-700';
    const textColor = 'text-white';
    const bagFillPercent = bagVolume ? (bagVolume / MAX_BAG_VOLUME) * 100 : Math.random() * 40 + 40;

    const adjustmentAmount = useMemo(() => {
        switch (label) {
            case 'BFR': return 10;
            case 'PBP': return 100;
            case 'Dialysate': return 100;
            case 'Replacement': return 100;
            case 'PFR': return 50;
            case 'Syringe': return 0.5;
            default: return 10;
        }
    }, [label]);

    const pKey = paramKey || label.toLowerCase();
    const buttonIdUp = `${pKey}_adjust_up`;
    const buttonIdDown = `${pKey}_adjust_down`;

    return (
        <div className="flex flex-col items-center relative cursor-pointer group" onClick={onClick}>
            {bagType && (
                <div className="w-24 h-28 bg-slate-700 bg-opacity-50 border-2 border-slate-600 rounded-lg mb-2 flex items-end relative overflow-hidden transition-transform duration-200 group-hover:scale-105">
                    <div className={`w-full ${bgColorMap[color]} rounded-b-md transition-all duration-500`} style={{ height: `${bagFillPercent}%` }}></div>
                </div>
            )}
            {label === 'Syringe' && (
                <div className="w-12 h-32 mb-2 flex flex-col justify-end items-center transition-transform duration-200 group-hover:scale-105">
                    <div className="w-2 h-20 bg-slate-400"></div>
                    <div className="w-10 h-10 bg-sky-500 border-2 border-sky-300 rounded-t-md shadow-inner"></div>
                    <div className="w-12 h-2 bg-slate-500 rounded-b-md"></div>
                </div>
            )}
            <div className={`relative flex flex-col items-center justify-center p-3 rounded-lg shadow-lg transition-all duration-200 group-hover:scale-105 group-hover:shadow-xl bg-gradient-to-br ${gradient} border-2 ${borderColor}`}>
                <span className="text-base font-semibold text-gray-200">{label}</span>
                <span className={`text-4xl font-bold font-mono ${textColor}`}>{value.toFixed(1)}</span>
                <span className={`text-sm ${textColor}`}>{unit}</span>
                {showButtons && (
                    <div className="absolute -right-10 top-1/2 -translate-y-1/2 flex flex-col space-y-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <button 
                            onClick={(e) => { e.stopPropagation(); onAdjust(adjustmentAmount); }} 
                            id={buttonIdUp}
                            className={`w-8 h-8 bg-slate-600 text-white rounded-full flex items-center justify-center text-xl hover:bg-slate-500 transition-colors shadow-lg hover:scale-110 ${currentTargetId === buttonIdUp ? 'glow-effect' : ''}`}
                        >
                            +
                        </button>
                        <button 
                            onClick={(e) => { e.stopPropagation(); onAdjust(-adjustmentAmount); }} 
                            id={buttonIdDown}
                            className={`w-8 h-8 bg-slate-600 text-white rounded-full flex items-center justify-center text-xl hover:bg-slate-500 transition-colors shadow-lg hover:scale-110 ${currentTargetId === buttonIdDown ? 'glow-effect' : ''}`}
                        >
                            -
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// Helper component to render learning points with simple markdown
const LearningPoint = ({ text }) => {
  const lines = text.split('\n');
  return (
    <div className="text-slate-300 text-lg">
      {lines.map((line, index) => {
        if (line.startsWith('**') && line.endsWith('**')) {
          return (
            <strong key={index} className="text-sky-300 font-semibold text-xl block mt-4 mb-2">
              {line.substring(2, line.length - 2)}
            </strong>
          );
        }
        if (line.trim() === '') {
            return <div key={index} className="h-3" />; // For newlines
        }
        return (
          <p key={index} className="ml-2 text-base">
            {line}
          </p>
        );
      })}
    </div>
  );
};

const ScoreModal = ({ score, log, onClose }) => {
    if (!score) return null;
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl p-8 w-full max-w-2xl border border-slate-700/50">
                <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center border-2 border-emerald-500">
                         <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                         </svg>
                    </div>
                    <div className="ml-4">
                         <h2 className="text-3xl font-bold text-white tracking-wide">Scenario Complete!</h2>
                         <p className="text-lg text-slate-400">{score.scenarioName}</p>
                    </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 my-6">
                    <div className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
                        <div className="text-sm font-medium text-slate-400 uppercase tracking-wider">Time to Resolve</div>
                        <div className="text-3xl font-bold text-white font-mono mt-1">{score.time} <span className="text-xl text-slate-400">seconds</span></div>
                    </div>
                     <div className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
                        <div className="text-sm font-medium text-slate-400 uppercase tracking-wider">Incorrect Actions</div>
                        <div className={`text-3xl font-bold font-mono mt-1 ${score.errors > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{score.errors}</div>
                    </div>
                </div>

                {score.errorsByType && Object.keys(score.errorsByType).length > 0 && (
                    <div className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4 mb-6">
                        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">Where the errors were</h3>
                        <div className="space-y-2">
                            {Object.entries(score.errorsByType).map(([type, n]) => {
                                const meta = {
                                    unsafe_restart:       { label: 'Restarted therapy without fixing the cause', severe: true },
                                    premature_set_change: { label: 'Changed the set on a salvageable circuit',    severe: true },
                                    tier_error:           { label: 'Stopped or started the blood pump manually',  severe: true },
                                    wrong_direction:      { label: 'Adjusted a rate the wrong way',               severe: true },
                                    sequence_error:       { label: 'Right action, wrong point in the sequence',   severe: false },
                                    unindicated_action:   { label: 'Checked something nothing pointed to',        severe: false },
                                }[type] || { label: type.replace(/_/g, ' '), severe: false };
                                return (
                                    <div key={type} className="flex justify-between items-center">
                                        <span className={`text-base ${meta.severe ? 'text-amber-300' : 'text-slate-400'}`}>
                                            {meta.label}
                                        </span>
                                        <span className={`font-mono font-bold ${meta.severe ? 'text-amber-300' : 'text-slate-400'}`}>{n}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {score.learningPoint && (
                     <div className="bg-gradient-to-br from-slate-900 to-slate-800/50 border border-slate-700 p-4 rounded-lg mb-6">
                         <LearningPoint text={score.learningPoint} />
                    </div>
                )}
                
                <div className="bg-slate-900/50 p-3 rounded-lg max-h-40 overflow-y-auto mb-6 border border-slate-700/50">
                    <h3 className="font-semibold text-slate-400 mb-2 px-1">Action Log:</h3>
                    {log.map((item, index) => (
                        <p key={index} className={`font-mono text-sm px-1 py-0.5 rounded ${item.isCorrect === false ? 'text-red-400' : 'text-slate-300'}`}>
                            - {item.action}
                        </p>
                    ))}
                </div>

                <button 
                    onClick={onClose} 
                    className="w-full bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-bold py-3 px-4 rounded-lg text-lg transition-all duration-200 shadow-lg hover:shadow-blue-500/30 hover:scale-105"
                >
                    Start New Scenario
                </button>
            </div>
        </div>
    );
};

const FluidBagControl = ({ name, color, volume, onBagChange, currentTargetId }) => {
    const percentage = (volume / MAX_BAG_VOLUME) * 100;
    const targetId = `change_${name.toLowerCase().substring(0, 3)}_bag`;
    return (
        <div className="bg-slate-900 p-4 rounded-lg flex flex-col items-center space-y-3 shadow-inner">
            <h3 className="font-bold text-lg">{name}</h3>
            <div className="w-24 h-32 bg-slate-700 border-2 border-slate-600 rounded-lg flex items-end relative overflow-hidden">
                <div className={`${color} w-full transition-all duration-500`} style={{ height: `${percentage}%` }}></div>
            </div>
            <p className="text-slate-400">{volume.toFixed(0)} / {MAX_BAG_VOLUME} mL</p>
            <button
                onClick={onBagChange}
                className={`bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold py-2 px-4 rounded w-full transition-all duration-200 shadow-lg ${currentTargetId === targetId ? 'glow-effect-box' : ''}`}
            >
                Change Bag
            </button>
        </div>
    );
};


const FluidsModal = ({ isOpen, onClose, bagVolumes, onBagChange, therapyMode, currentTargetId, onReverseLines }) => {
    if (!isOpen) return null;

    const allFluidConfig = {
        pbp: { name: 'PBP', color: 'bg-pink-500' },
        dia: { name: 'Dialysate', color: 'bg-emerald-500' },
        rep: { name: 'Replacement', color: 'bg-purple-500' },
    };

    const fluidConfig = {};
    if (therapyMode !== 'CVVHD') {
        fluidConfig.pbp = allFluidConfig.pbp;
        fluidConfig.rep = allFluidConfig.rep;
    }
    if (therapyMode !== 'CVVH') {
        fluidConfig.dia = allFluidConfig.dia;
    }

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-3xl border border-slate-600/50">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-sky-300">Fluid Management</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white text-3xl leading-none">&times;</button>
                </div>
                <div className="flex justify-around items-start">
                    {Object.keys(fluidConfig).map(key => {
                        const config = fluidConfig[key];
                        return (
                           <FluidBagControl
                                key={key}
                                name={config.name}
                                color={config.color}
                                volume={bagVolumes[key] || 0}
                                onBagChange={() => onBagChange(key)}
                                currentTargetId={currentTargetId}
                           />
                        )
                    })}
                </div>
                {/* Button is no longer here */}
            </div>
        </div>
    );
};

const InfoModal = ({ content, onClose }) => {
    if (!content) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4" onClick={onClose}>
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md border border-slate-600/50" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-sky-300">{content.title}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white text-3xl leading-none">&times;</button>
                </div>
                <p className="text-slate-300 whitespace-pre-wrap">{content.description}</p>
            </div>
        </div>
    );
};

const ModeSelectionModal = ({ isOpen, onClose, currentMode, onSelectMode }) => {
    if (!isOpen) return null;
    const modes = ['CVVHDF', 'CVVH', 'CVVHD'];

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-sm border border-slate-600/50">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-sky-300">Select Therapy Mode</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white text-3xl leading-none">&times;</button>
                </div>
                <div className="flex flex-col space-y-3">
                    {modes.map(mode => (
                        <button
                            key={mode}
                            onClick={() => onSelectMode(mode)}
                            className={`px-4 py-3 rounded-md font-semibold text-white text-lg transition-colors duration-200 ${currentMode === mode ? 'bg-gradient-to-br from-blue-500 to-blue-700' : 'bg-slate-700 hover:bg-slate-600'}`}
                        >
                            {mode}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const FeedbackModal = ({ isOpen, onClose, db, isAuthReady, userId, sessionId }) => {
    if (!isOpen) return null;

    const [ratings, setRatings] = useState({ usefulness: 0, useAgain: 0, recommend: 0 });
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!db || !isAuthReady) {
            console.error("Firestore not ready");
            onClose();
            return;
        };
        setIsSubmitting(true);
        const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
        try {
            await addDoc(collection(db, `/artifacts/${appId}/public/data/feedback`), {
                schemaVersion: 2,
                userId,
                sessionId,
                ratings,
                comments,
                timestamp: new Date()
            });
        } catch (error) {
            console.error("Error writing feedback: ", error);
        } finally {
            setIsSubmitting(false);
            onClose();
        }
    };

    const RatingScale = ({ question, label, minLabel, maxLabel }) => (
        <div>
            <label className="block text-slate-300 mb-2">{label}</label>
            <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">{minLabel}</span>
                {[1, 2, 3, 4, 5].map(value => (
                    <button
                        key={value}
                        onClick={() => setRatings(prev => ({...prev, [question]: value}))}
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                            ratings[question] === value
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                    >
                        {value}
                    </button>
                ))}
                <span className="text-xs text-slate-400">{maxLabel}</span>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-lg border border-slate-600/50">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-sky-300">Quick Feedback</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white text-3xl leading-none">&times;</button>

                </div>
                <div className="space-y-6">
                    <RatingScale
                        question="usefulness"
                        label="How useful was this simulator as a learning tool?"
                        minLabel="Not at all"
                        maxLabel="Very useful"
                    />
                    <RatingScale
                        question="useAgain"
                        label="How likely are you to use this simulator again?"
                        minLabel="Not at all"
                        maxLabel="Very likely"
                    />
                    <RatingScale
                        question="recommend"
                        label="How likely are you to recommend this to someone else?"
                        minLabel="Not at all"
                        maxLabel="Very likely"
                    />
                    <div>
                        <label htmlFor="comments" className="block text-slate-300 mb-2">Any other comments or suggestions (optional)</label>
                        <textarea
                            id="comments"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows="3"
                            className="w-full bg-slate-900 border border-slate-600 rounded-md p-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        ></textarea>
                    </div>
                    <button onClick={handleSubmit} disabled={isSubmitting} className="w-full bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-bold py-3 px-4 rounded transition-all duration-200 shadow-lg disabled:opacity-50">
                        {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- NEW COMPONENT: Opening Screen ---
const OpeningScreen = ({ onContinue, db, isAuthReady, userId, sessionId }) => {
    const [animationStep, setAnimationStep] = useState(0);
    const [role, setRole] = useState('');
    const [area, setArea] = useState('');
    const [country, setCountry] = useState('');
    const [pathLength, setPathLength] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const pathRef = useCallback(node => {
        if (node !== null) {
            setPathLength(node.getTotalLength());
        }
    }, []);

    useEffect(() => {
        const timer1 = setTimeout(() => setAnimationStep(1), 500); // Start animation
        const timer2 = setTimeout(() => setAnimationStep(2), 2500); // Show title
        const timer3 = setTimeout(() => setAnimationStep(3), 3000); // Show form
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, []);
    
    const handleContinueClick = async () => {
        if (isContinueDisabled) return; // Prevent clicks when disabled

        if (db && isAuthReady) {
            setIsSubmitting(true);
            const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';

            try {
                await addDoc(collection(db, `/artifacts/${appId}/public/data/registrations`), {
                    schemaVersion: 2,
                    userId,      // the join key to this person's analytics records
                    sessionId,
                    role,
                    area,
                    country,
                    timestamp: new Date()
                });
            } catch (error) {
                console.error("Error writing registration info: ", error);
            } finally {
                setIsSubmitting(false);
                onContinue();
            }
        } else {
             if (!isAuthReady) {
                 console.error("Firestore not ready. Cannot continue.");
                 return;
             }
            // If Firestore is not available, just proceed.
            console.warn("Firestore not available. Proceeding without saving registration.");
            onContinue();
        }
    };

    const isContinueDisabled = !role || !area || isSubmitting || !isAuthReady;
    const rnPath = "M 10 50 L 40 50 Q 60 10, 80 50 T 110 50"; // Updated path for a generic loop

    return (
        <div className="bg-slate-900 text-white min-h-screen font-sans flex flex-col justify-center items-center p-4">
            <style>{`
                @keyframes flow {
                    0% { offset-distance: 0%; }
                    100% { offset-distance: 100%; }
                }
                 @keyframes draw {
                    to { stroke-dashoffset: 0; }
                }
                .blood-cell {
                    width: 15px;
                    height: 15px;
                    background: #ef4444; /* red-500 */
                    border-radius: 50%;
                    position: absolute;
                    offset-path: path("${rnPath}");
                    animation: flow 2s linear forwards;
                    opacity: ${animationStep === 1 ? 1 : 0};
                    transition: opacity 0.3s;
                }
                .rn-path {
                    stroke-dasharray: ${pathLength};
                    stroke-dashoffset: ${pathLength};
                    animation: draw 2s linear forwards;
                    animation-play-state: ${animationStep >= 1 ? 'running' : 'paused'};
                }
            `}</style>
            
            <div className={`transition-all duration-500 ${animationStep >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <h1 className="text-5xl font-bold text-center text-sky-300 mb-8">
                    CRRT Alarm Simulator
                </h1>
            </div>

            <div className={`w-full max-w-md transition-opacity duration-500 ${animationStep >= 1 && animationStep < 3 ? 'opacity-100' : 'opacity-0'}`}>
                 <div className="relative w-full h-24">
                     <svg width="120" height="100" viewBox="0 0 120 100" className="absolute top-0 left-1/2 -translate-x-1/2">
                         <path ref={pathRef} d={rnPath} stroke="#475569" strokeWidth="4" fill="none" className="rn-path" />
                    </svg>
                     <div className="blood-cell" style={{animationDelay: '0s'}}></div>
                     <div className="blood-cell" style={{animationDelay: '0.2s'}}></div>
                     <div className="blood-cell" style={{animationDelay: '0.4s'}}></div>
                </div>
            </div>
            
            <div className={`w-full max-w-lg p-8 bg-slate-800/50 border border-slate-700/50 rounded-lg shadow-2xl transition-opacity duration-500 ${animationStep === 3 ? 'opacity-100' : 'opacity-0 -translate-y-4'}`}>
                <p className="text-center text-slate-400 mb-6">
                    This tool is intended for education purposes only and should not replace clinical decision-making, nor should it replace institutional protocols as your centre.
                </p>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="role" className="block text-sm font-semibold text-slate-300 mb-1">Role</label>
                        <select id="role" value={role} onChange={e => setRole(e.target.value)} className="w-full p-2 bg-slate-700 rounded border border-slate-600">
                            <option value="">Select a role...</option>
                            <option>Nurse</option>
                            <option>Nursing Student</option>
                            <option>Fellow</option>
                            <option>Resident/Student</option>
                            <option>Physician</option>
                            <option>Other</option>
                        </select>
                    </div>
                     <div>
                        <label htmlFor="area" className="block text-sm font-semibold text-slate-300 mb-1">Area</label>
                        <select id="area" value={area} onChange={e => setArea(e.target.value)} className="w-full p-2 bg-slate-700 rounded border border-slate-600">
                            <option value="">Select an area...</option>
                            <option>Paediatric Nephrology</option>
                            <option>Paediatric Critical Care</option>
                            <option>Adult Nephrology</option>
                            <option>Adult Critical Care</option>
                            <option>Other</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="country" className="block text-sm font-semibold text-slate-300 mb-1">Country (Optional)</label>
                        <input type="text" id="country" value={country} onChange={e => setCountry(e.target.value)} className="w-full p-2 bg-slate-700 rounded border border-slate-600" />
                    </div>
                    <button 
                        onClick={handleContinueClick} 
                        disabled={isContinueDisabled}
                        className="w-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-blue-600 hover:to-blue-800 transition-all duration-200"
                    >
                        {!isAuthReady ? 'Connecting...' : (isSubmitting ? 'Loading...' : 'Continue')}
                    </button>
                </div>
            </div>

            <p className={`text-center text-xs text-slate-500 mt-8 transition-opacity duration-500 ${animationStep >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                Created by Dr. Amrit Kirpalani MD, MEHP, FRCPC. For educational purposes only. Not intended for clinical decision-making.
            </p>
        </div>
    );
};

const PracticeCompleteModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md border border-slate-600/50">
                <h2 className="text-2xl font-bold text-sky-300 mb-4">Practice Round Complete!</h2>
                <p className="text-slate-300 mb-6">
                    You have successfully completed all available scenarios for this therapy mode. The scenarios will now be reshuffled for a new round.
                </p>
                <button onClick={onClose} className="w-full bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-bold py-2 px-4 rounded transition-all duration-200 shadow-lg">
                    Start New Round
                </button>
            </div>
        </div>
    );
};

// --- New Helper Component for Training Buttons ---
const ScenarioButton = ({ scenarioKey, scenario, currentScenario, onClick, therapyMode }) => {
  // This logic is now more complex because the `scenario` object might be a
  // a real scenario (like 'blood_leak') or a category object (like 'high_tmp')
  
  let name;
  let isActive = false;
  
  if (scenario.keys) {
      // It's a category object (e.g., scenarioCategories['high_tmp'])
      name = scenario.name;
      isActive = currentScenario.categoryName === name;
  } else {
      // It's a single scenario object (e.g., scenarios['blood_leak'])
      name = scenario.name;
      isActive = currentScenario.name === name;
  }

  // Hide empty dialysate button if in CVVH mode
  if (therapyMode === 'CVVH' && scenarioKey === 'empty_dialysate') return null;
  // Hide PBP/Replacement scenarios if in CVVHD mode
  if (therapyMode === 'CVVHD' && (scenarioKey === 'pbp_related_scenario' || scenarioKey === 'empty_pbp_bag' || scenarioKey === 'empty_replacement_bag')) return null;


  return (
    <button 
        key={scenarioKey} 
        onClick={() => onClick(scenarioKey)}
        className={`px-4 py-2 rounded-lg font-medium text-slate-200 transition-all duration-200 shadow-md hover:scale-105 ${isActive ? 'bg-gradient-to-br from-blue-500 to-blue-700 ring-2 ring-sky-300 text-white font-semibold shadow-lg' : 'bg-gradient-to-b from-slate-700 to-slate-800 border border-slate-600 hover:border-slate-400 hover:shadow-lg hover:-translate-y-px hover:text-white'}`}
    >
        {name}
    </button>
  );
};


// --- The Main App ---
export default function App() {
  const [appState, setAppState] = useState('splash'); // splash | simulator
  const [params, setParams] = useState({ pbp: 2000, bfr: 250, syringe: 0.0, pfr: 150, dia: 1000, rep: 500 });
  const [bagVolumes, setBagVolumes] = useState({ dia: MAX_BAG_VOLUME, rep: MAX_BAG_VOLUME, pbp: MAX_BAG_VOLUME });
  const [pressures, setPressures] = useState(scenarios.normal.initialState.pressures);
  const [effluentDose, setEffluentDose] = useState(0);
  const [currentScenario, setCurrentScenario] = useState(scenarios.normal);
  const [troubleshootingStep, setTroubleshootingStep] = useState(0);
  const [performanceLog, setPerformanceLog] = useState([]);
  const [scenarioStartTime, setScenarioStartTime] = useState(null);
  const [finalScore, setFinalScore] = useState(null);
  const [isFluidsModalOpen, setIsFluidsModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState(null);
  const [isPumpRunning, setIsPumpRunning] = useState(true);
  const [fluidPumpsRunning, setFluidPumpsRunning] = useState(true);
  const [clampClosed, setClampClosed] = useState(false);
  const [therapyMode, setTherapyMode] = useState('CVVHDF');
  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [mode, setMode] = useState('training');
  const [firebase, setFirebase] = useState({ db: null, analytics: null });
  const [isAuthReady, setIsAuthReady] = useState(false);
  // Firebase persists the anonymous UID in browser storage, so the same person
  // keeps the same id across visits. This is what links a registration to the
  // performance sessions that follow it.
  const [userId, setUserId] = useState(null);
  const [sessionId] = useState(() => (
      typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  ));
  const [attemptCounts, setAttemptCounts] = useState({});
  const [sessionScenarioIndex, setSessionScenarioIndex] = useState(0);
  const [practiceScenarioQueue, setPracticeScenarioQueue] = useState([]);
  const [currentPracticeIndex, setCurrentPracticeIndex] = useState(0);
  const [showPracticeCompleteModal, setShowPracticeCompleteModal] = useState(false);
  const [activeTrainingGroup, setActiveTrainingGroup] = useState(null); // State for new accordion menu
  const [screenFlash, setScreenFlash] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [pressureRelief, setPressureRelief] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true); // New state for minimizing controls


  // Effect to load Google Fonts & Initialize Firebase
  useEffect(() => {
    const link1 = document.createElement('link');
    link1.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap';
    link1.rel = 'stylesheet';
    document.head.appendChild(link1);

    const link2 = document.createElement('link');
    link2.href = 'https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;700&display=swap';
    link2.rel = 'stylesheet';
    document.head.appendChild(link2);

    try {
        const app = initializeApp(firebaseConfig);
        const db = getFirestore(app);
        const analytics = getAnalytics(app);
        const auth = getAuth(app);
        setFirebase({ db, analytics });

        onAuthStateChanged(auth, async (user) => {
            if (!user) {
                try {
                    const cred = await signInAnonymously(auth);
                    setUserId(cred.user.uid);
                } catch (authError) {
                    console.error("Firebase anonymous auth failed:", authError);
                }
            } else {
                setUserId(user.uid);
            }
            setIsAuthReady(true);
        });
    } catch (error) {
        console.error("Firebase initialization failed:", error);
        setIsAuthReady(true);
    }

  }, []);


  // Error types are recorded separately so that a clinically dangerous action
  // (restarting a circuit that must be changed) is distinguishable from a misclick.
  const logAction = useCallback((action, isCorrect = null, errorType = null) => {
      setPerformanceLog(prev => [...prev, { action, isCorrect, errorType, timestamp: Date.now() }]);
  }, []);

  const handleScenarioChange = useCallback((scenarioKey) => {
      let finalScenarioKey = scenarioKey;
      
      // If the key is a category key (e.g., 'access_occlusion')
      if (scenarioCategories[scenarioKey]) {
          if (mode === 'training') {
              // In TRAINING mode, always pick the FIRST scenario from the category's list
              finalScenarioKey = scenarioCategories[scenarioKey].keys[0];
          } else {
              // In PRACTICE mode, pick a RANDOM scenario from the category's list
              const subKeys = scenarioCategories[scenarioKey].keys;
              finalScenarioKey = subKeys[Math.floor(Math.random() * subKeys.length)];
          }
      }

      const scenario = scenarios[finalScenarioKey];
      setCurrentScenario(scenario);
      setTroubleshootingStep(0);
      setPerformanceLog([]);
      setFinalScore(null);
      setActionFeedback(null);
      setPressureRelief(0);

      // The machine responds on its own, according to the alarm tier.
      const tier = scenario.tier || null;
      if (tier === 'warning') {
          setIsPumpRunning(false);
          setFluidPumpsRunning(false);
          setClampClosed(true);
      } else if (tier === 'caution') {
          setIsPumpRunning(true);
          setFluidPumpsRunning(false);
          setClampClosed(false);
      } else {
          setIsPumpRunning(true);
          setFluidPumpsRunning(true);
          setClampClosed(false);
      }
      
      if (scenario.initialState.troubleshooting) {
          setScenarioStartTime(Date.now());
          console.log(`SIMULATOR: Scenario started - ${scenario.name}.`);
          setTimeout(() => console.log(`ALARM: ${scenario.initialState.troubleshooting.problem}`), 500);
          setPressures(scenario.initialState.pressures);
          if (mode === 'training') {
             setControlsVisible(false); // Minimize controls when a scenario starts
          }
      } else {
          setScenarioStartTime(null);
          console.log(`SIMULATOR: Normal operation resumed.`);
          setPressures(scenarios.normal.initialState.pressures);
      }
  }, [mode]); // Add mode dependency

  const completeScenario = useCallback(async () => { // Made async
    if (!scenarioStartTime) return;
    const endTime = Date.now();
    const timeToResolve = ((endTime - scenarioStartTime) / 1000).toFixed(1);
    const errorEvents = performanceLog.filter(log => log.isCorrect === false);
    const errors = errorEvents.length;
    const errorsByType = errorEvents.reduce((acc, log) => {
        const t = log.errorType || 'unindicated_action';
        acc[t] = (acc[t] || 0) + 1;
        return acc;
    }, {});

    setFinalScore({
        scenarioName: currentScenario.name,
        time: timeToResolve,
        errors: errors,
        errorsByType,
        learningPoint: currentScenario.initialState.troubleshooting.learningPoint
    });

    const successMessage = currentScenario.initialState.troubleshooting.successMessage;
    console.log(`SUCCESS: ${successMessage}`);
    logAction(successMessage, true);

    // How many times this person has met this scenario in this visit. Combined with
    // userId and timestamp, the cumulative attempt across visits is recoverable at
    // analysis time without the app ever needing to read the database back.
    const attemptKey = `${mode}:${currentScenario.name}`;
    const attemptNo = (attemptCounts[attemptKey] || 0) + 1;
    setAttemptCounts(prev => ({ ...prev, [attemptKey]: attemptNo }));
    setSessionScenarioIndex(prev => prev + 1);

    // --- ADDED ANALYTICS ---
    if (firebase.db && isAuthReady) {
      const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
      try {
        // Create a new document in a new "analytics" collection
        await addDoc(collection(firebase.db, `/artifacts/${appId}/public/data/analytics`), {
          schemaVersion: 2,
          userId: userId,              // links this session to a registration
          sessionId: sessionId,        // one browser visit
          attemptNumber: attemptNo,    // nth time this person has done this scenario, this visit
          sessionScenarioIndex: sessionScenarioIndex + 1, // nth scenario overall, this visit
          scenarioName: currentScenario.name,
          tier: currentScenario.tier || null,
          mode: mode,
          therapyMode: therapyMode,
          timeToResolve: parseFloat(timeToResolve), // Send as a number
          errors: errors,
          errorsByType: errorsByType,
          actionLog: performanceLog, // This saves the entire click-by-click log
          timestamp: new Date()
        });
      } catch (error) {
        console.error("Error writing analytics: ", error);
      }
    }
    // --- END OF ANALYTICS ---
    
    setCurrentScenario(scenarios.normal);
    setTroubleshootingStep(0);
    setIsPumpRunning(true);
    setFluidPumpsRunning(true);
    setClampClosed(false);
  }, [scenarioStartTime, performanceLog, currentScenario, logAction, firebase.db, isAuthReady, mode,
      therapyMode, userId, sessionId, attemptCounts, sessionScenarioIndex]);

  // Dynamic Pressure Calculation Effect
  useEffect(() => {
    if (!isPumpRunning) {
        // The blood pump is stopped. Hold the pressures at their alarm values
        // rather than zeroing them — the abnormal reading is what the learner
        // is reasoning about, and it should stay on screen.
        return;
    }
    const { pbp, bfr, pfr, rep, dia } = params;

    // 1. Calculate base pressures that are always dependent on BFR
    const baseAccess = -30 - (bfr / 3);
    const baseReturn = 50 + (bfr / 8);
    const baseTmp = 110 + (bfr / 10);
    const baseDeltaP = 30 + (bfr / 5);

    // Modify based on other params for a more dynamic simulation
    const accessWithPbp = baseAccess + (pbp / 200); // PBP slightly improves (less negative) access pressure
    const totalEffluentForTmp = pfr + rep + dia;
    const tmpWithEffluent = baseTmp + (totalEffluentForTmp / 50); // TMP increases with total fluid removal rate

    const basePressures = {
      access: Math.round(accessWithPbp),
      return: Math.round(baseReturn),
      tmp: Math.round(tmpWithEffluent),
      deltaP: Math.round(baseDeltaP),
    };

    let finalPressures = basePressures;

    // 2. Apply hard modifiers from the active scenario if it's not normal operation
    if (currentScenario && currentScenario.name !== "Normal Operation" && currentScenario.initialState && currentScenario.initialState.pressures) {
        // Pressures start at the alarm values and travel back toward normal as the
        // learner corrects the cause, so the numbers confirm the fix rather than
        // the workflow counter being the only feedback.
        const onset = currentScenario.initialState.pressures;
        const target = scenarios.normal.initialState.pressures;
        const k = Math.min(1, Math.max(0, pressureRelief));
        finalPressures = {
            access: Math.round(onset.access + (target.access - onset.access) * k),
            return: Math.round(onset.return + (target.return - onset.return) * k),
            tmp: Math.round(onset.tmp + (target.tmp - onset.tmp) * k),
            deltaP: Math.round(onset.deltaP + (target.deltaP - onset.deltaP) * k),
        };
    }
    
    setPressures(finalPressures);

  }, [params, currentScenario, isPumpRunning, pressureRelief]);

  // Effluent Dose Calculation Effect
  useEffect(() => {
      const patientWeightKg = 70; // Assumed patient weight for dose calculation
      const totalEffluentMlHr = params.dia + params.rep + params.pfr + params.pbp;
      const dose = totalEffluentMlHr / patientWeightKg;
      setEffluentDose(dose);
  }, [params.dia, params.rep, params.pfr, params.pbp]);

  // Fluid Bag Depletion Effect
  useEffect(() => {
    if (!fluidPumpsRunning) return;

    const interval = setInterval(() => {
        if(currentScenario.name.includes("Empty")) return;

        setBagVolumes(prev => {
            const newVolumes = { ...prev };
            if (therapyMode !== 'CVVH') {
                newVolumes.dia -= params.dia / 3600; 
            }
            if (therapyMode !== 'CVVHD') {
                newVolumes.rep -= params.rep / 3600;
                newVolumes.pbp -= params.pbp / 3600;
            }
            
            if (newVolumes.dia <= 0 && currentScenario.name !== 'Empty Dialysate Bag' && therapyMode !== 'CVVH') {
                handleScenarioChange('empty_dialysate');
                newVolumes.dia = 0;
            }
            if (newVolumes.pbp <= 0 && currentScenario.name !== 'Empty PBP Bag' && therapyMode !== 'CVVHD') {
                handleScenarioChange('empty_pbp_bag');
                newVolumes.pbp = 0;
            }
            if (newVolumes.rep <= 0 && currentScenario.name !== 'Empty Replacement Bag' && therapyMode !== 'CVVHD') {
                handleScenarioChange('empty_replacement_bag');
                newVolumes.rep = 0;
            }
            return newVolumes;
        });
    }, 1000);
    return () => clearInterval(interval);
  }, [params, currentScenario.name, handleScenarioChange, fluidPumpsRunning, therapyMode]);
  
  const handleAdjust = (param, amount) => {
    // Setpoints are freely adjustable while the blood pump is stopped, exactly as
    // they are on the machine. Several alarms stop the pump and then require a
    // rate change, so this must not be blocked.
    setParams(prev => {
        const newValue = Math.max(0, prev[param] + amount);
        return { ...prev, [param]: newValue };
    });

    const direction = amount > 0 ? 'up' : 'down';
    logAction(`Adjusted ${param.toUpperCase()} ${direction} to ${Math.max(0, params[param] + amount).toFixed(1)}`);

    // Every adjustment is routed through handleAction, so an adjustment in the
    // wrong direction or on the wrong parameter is scored as an error rather
    // than passing silently.
    if (currentScenario.initialState.troubleshooting) {
        handleAction(`${param}_adjust_${direction}`);
    }
  };
  
  const handleAction = useCallback((targetId) => {
      // No active alarm: the pump toggle behaves manually, as during normal running.
      if (!currentScenario.initialState.troubleshooting) {
          if(targetId === 'blood-pump') {
              const nextState = !isPumpRunning;
              setIsPumpRunning(nextState);
              setFluidPumpsRunning(nextState);
              const actionText = nextState ? "Resumed" : "Stopped";
              logAction(`Pump ${actionText}`);
          } else {
            console.log("No active issue to troubleshoot.");
          }
          return;
      }

      const workflow = currentScenario.initialState.troubleshooting.workflow;
      if (troubleshootingStep >= workflow.length) return;

      const currentStep = workflow[troubleshootingStep];
      const tier = currentScenario.tier;

      // The machine controls the blood pump during an alarm. Touching it manually
      // is either redundant (it has already stopped) or unsafe (it is running for
      // a reason), so it is always scored rather than silently toggling.
      if (targetId === 'blood-pump') {
          if (tier === 'warning') {
              logAction('Incorrect action: the machine has already stopped the blood pump', false, 'tier_error');
          } else {
              logAction(`Incorrect action: stopped the blood pump during a ${tier} alarm — it is running for a reason`, false, 'tier_error');
          }
          setScreenFlash(true);
          setTimeout(() => setScreenFlash(false), 500);
          return;
      }

      // RESUME THERAPY clears the alarm and restarts whatever stopped. Pressed before the
      // cause is corrected, the alarm simply comes back — as it does on the machine.
      if (targetId === 'key_continue' && currentStep.target !== 'key_continue') {
          logAction('Incorrect action: pressed Resume Therapy before resolving the cause — the alarm returns', false, 'unsafe_restart');
          if (tier === 'warning') { setIsPumpRunning(false); setFluidPumpsRunning(false); setClampClosed(true); }
          else if (tier === 'caution') { setFluidPumpsRunning(false); }
          setScreenFlash(true);
          setTimeout(() => setScreenFlash(false), 500);
          return;
      }

      // Changing the set on a circuit that could have been saved.
      if (targetId === 'key_change_set' && currentStep.target !== 'key_change_set') {
          logAction('Incorrect action: changed the set before the circuit was shown to be unsalvageable', false, 'premature_set_change');
          setScreenFlash(true);
          setTimeout(() => setScreenFlash(false), 500);
          return;
      }

      if (targetId === currentStep.target) {
          logAction(`Correct action: ${currentStep.text}`, true);

          // Tell the learner what they actually found. Without this, an inspection
          // is just a click that advances a counter, and there is no way to know
          // whether the problem has been solved or the search should continue.
          if (currentStep.finding) {
              setActionFeedback({ tone: 'found', text: currentStep.finding });
          } else {
              setActionFeedback(null);
          }

          // Corrective steps move the pressures back toward normal.
          if (typeof currentStep.relief === 'number') {
              setPressureRelief(prev => Math.max(prev, currentStep.relief));
          }

          // The terminal softkey restores the machine.
          if (targetId === 'key_continue') {
              setIsPumpRunning(true);
              setFluidPumpsRunning(true);
              setClampClosed(false);
          }
          if (targetId === 'key_change_set') {
              setIsPumpRunning(false);
              setFluidPumpsRunning(false);
              setClampClosed(true);
          }

          const nextStep = troubleshootingStep + 1;
          if (nextStep >= workflow.length) {
              completeScenario();
          } else {
              setTroubleshootingStep(nextStep);
          }
      } else {
          // Inspecting a component that is not the source still tells you something.
          const componentNames = {
              'access-line': 'Access line',
              'return-line': 'Return line',
              'effluent-line': 'Effluent line',
              'filter': 'Filter',
              'deaeration_chamber': 'De-aeration chamber',
          };
          if (componentNames[targetId]) {
              setActionFeedback({ tone: 'clear', text: `${componentNames[targetId]}: nothing abnormal found here.` });
          } else {
              setActionFeedback(null);
          }
          // Classify the mistake. A rate moved the wrong way, a step done out of
          // order, and an action nothing pointed to are different kinds of error.
          let errorType = 'unindicated_action';
          const rateMatch = /^([a-z]+)_adjust_(up|down)$/.exec(targetId);
          const expectedRate = /^([a-z]+)_adjust_(up|down)$/.exec(currentStep.target);
          if (rateMatch && expectedRate && rateMatch[1] === expectedRate[1]) {
              errorType = 'wrong_direction';
          } else if (workflow.some(s => s.target === targetId)) {
              errorType = 'sequence_error';
          }
          logAction(`Incorrect action (Clicked: ${targetId}, Expected: ${currentStep.target})`, false, errorType);
          setScreenFlash(true);
          setTimeout(() => setScreenFlash(false), 500);
          if (mode === 'practice') {
              console.log("Incorrect step.");
          } else {
              console.log("Incorrect step. Please follow the guidance.");
          }
      }
  }, [currentScenario, troubleshootingStep, logAction, completeScenario, isPumpRunning, mode]);
  
  const handleBagChange = (bagType) => {
    setBagVolumes(prev => ({ ...prev, [bagType]: MAX_BAG_VOLUME }));
    logAction(`Changed ${bagType.toUpperCase()} bag.`);

    const workflow = currentScenario?.initialState?.troubleshooting?.workflow;
    if (workflow && troubleshootingStep < workflow.length) {
        const currentStep = workflow[troubleshootingStep];
        const expectedTarget = `change_${bagType}_bag`;
        if (currentStep.target === expectedTarget) {
            handleAction(expectedTarget);
        }
    }
    setIsFluidsModalOpen(false);
  };

  const handleModeChange = (newMode) => {
    setTherapyMode(newMode);
    setParams(prev => {
        const newParams = {...prev};
        if (newMode === 'CVVH') {
            newParams.dia = 0; // No dialysate in CVVH
        }
        if (newMode === 'CVVHD') {
            newParams.pbp = 0; // No PBP in CVVHD
            newParams.rep = 0; // No Replacement in CVVHD
        }
        return newParams;
    });
    logAction(`Therapy mode changed to ${newMode}`);
    console.log(`Therapy mode changed to ${newMode}`);
    setIsModeModalOpen(false);
  };

  const shuffleArray = (array) => {
    let currentIndex = array.length,  randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
  };

  const startNewPracticeRound = () => {
    setShowPracticeCompleteModal(false);
    let practiceScenarios = Object.keys(scenarios).filter(key => 
        key !== 'normal'
    );
    if (therapyMode === 'CVVH') {
        practiceScenarios = practiceScenarios.filter(key => key !== 'empty_dialysate');
    }
    if (therapyMode === 'CVVHD') {
        practiceScenarios = practiceScenarios.filter(key => key !== 'empty_pbp_bag' && key !== 'empty_replacement_bag');
    }
    const queue = shuffleArray(practiceScenarios);
    setPracticeScenarioQueue(queue);
    
    if (queue.length > 0) {
      const nextScenarioKey = queue[0];
      handleScenarioChange(nextScenarioKey);
      setCurrentPracticeIndex(1);
      console.log("PRACTICE: New random scenario started.");
    } else {
        console.log("PRACTICE: No available scenarios for this therapy mode.");
    }
  };

  const handlePracticeScenarioChange = () => {
    if (currentPracticeIndex >= practiceScenarioQueue.length && practiceScenarioQueue.length > 0) {
      setShowPracticeCompleteModal(true);
      return;
    }
    
    const nextScenarioKey = practiceScenarioQueue[currentPracticeIndex];
    handleScenarioChange(nextScenarioKey);
    setCurrentPracticeIndex(prev => prev + 1);
  };

  const handleModeToggle = (newMode) => {
    if (mode === newMode) return;

    setMode(newMode);
    const newModeName = newMode === 'training' ? 'Review' : 'Practice';
    logAction(`Switched to ${newModeName} mode.`);
    console.log(`Switched to ${newModeName} mode.`);
    handleScenarioChange('normal');
    setActiveTrainingGroup(null); // Reset training group view
    setControlsVisible(true); // Always show controls when toggling mode

    if (newMode === 'practice') {
        let practiceScenarios = Object.keys(scenarios).filter(key => 
            key !== 'normal'
        );
        if (therapyMode === 'CVVH') {
            practiceScenarios = practiceScenarios.filter(key => key !== 'empty_dialysate');
        }
        if (therapyMode === 'CVVHD') {
            practiceScenarios = practiceScenarios.filter(key => key !== 'empty_pbp_bag' && key !== 'empty_replacement_bag');
        }
        setPracticeScenarioQueue(shuffleArray(practiceScenarios));
        setCurrentPracticeIndex(0);
    }
  };

  const closeScoreModal = () => {
    setFinalScore(null);
    setPerformanceLog([]);
    setActiveTrainingGroup(null); // Reset training group view
    setControlsVisible(true); // Show controls again after scenario
    
    console.log('Therapy Ongoing. Select a scenario.');

    if (mode === 'practice') {
        handlePracticeScenarioChange();
    } else {
        handleScenarioChange('normal');
    }
  }

  const currentStepText = currentScenario?.initialState?.troubleshooting?.workflow?.[troubleshootingStep]?.text;
  
  const currentTargetId = mode === 'training' && currentScenario?.initialState?.troubleshooting?.workflow?.[troubleshootingStep]?.target;

  if (appState === 'splash') {
    return <OpeningScreen 
        onContinue={() => setAppState('simulator')} 
        db={firebase.db} 
        isAuthReady={isAuthReady} 
        userId={userId}
        sessionId={sessionId}
    />;
  }

  return (
    <div className="bg-slate-900 min-h-screen flex items-center justify-center p-4">
      <style>{`
        :root {
          font-family: 'Inter', sans-serif;
        }
        .font-mono {
          font-family: 'Roboto Mono', monospace;
        }
        @keyframes glow {
            0%, 100% { box-shadow: 0 0 8px #38bdf8, 0 0 16px #38bdf8, 0 0 24px #38bdf8; }
            50% { box-shadow: 0 0 24px #38bdf8, 0 0 40px #38bdf8, 0 0 50px #38bdf8; }
        }
        .glow-effect {
          animation: glow 1.5s infinite ease-in-out;
          border-radius: 9999px; /* For circular/pill elements */
        }
        .glow-effect-box {
          animation: glow 1.5s infinite ease-in-out;
          border-radius: 0.75rem; /* For boxy elements */
        }
        
        @keyframes pumpStopFlash {
          0%, 100% { background-color: rgba(245,158,11,0.18); border-color: rgb(245,158,11); }
          50%      { background-color: rgba(245,158,11,0.38); border-color: rgb(252,211,77); }
        }
        .pump-stopped-banner {
          animation: pumpStopFlash 1.1s infinite ease-in-out;
        }

        @keyframes flash {
          0% { opacity: 0.7; }
          100% { opacity: 0; }
        }
        .screen-flash {
          animation: flash 0.5s ease-out;
        }

        @keyframes particle-flow-red {
            from { offset-distance: 0%; opacity: 1; }
            to { offset-distance: 100%; opacity: 0; }
        }
        .particle-red {
            offset-path: path('M 70 190 L 198 190 L 198 305 L 320 190 L 490 190');
            animation: particle-flow-red 4s linear infinite;
        }

        @keyframes particle-flow-blue {
            from { offset-distance: 0%; opacity: 1; }
            to { offset-distance: 100%; opacity: 0; }
        }
        .particle-blue {
            offset-path: path('M 620 190 L 988 190 L 988 305');
            animation: particle-flow-blue 3s linear infinite;
        }
      `}</style>
      {screenFlash && (
          <div className="absolute inset-0 bg-red-600/70 z-[100] screen-flash pointer-events-none"></div>
      )}
      <ScoreModal score={finalScore} log={performanceLog} onClose={closeScoreModal} />
      <PracticeCompleteModal isOpen={showPracticeCompleteModal} onClose={startNewPracticeRound} />
      <FluidsModal 
        isOpen={isFluidsModalOpen}
        onClose={() => setIsFluidsModalOpen(false)}
        bagVolumes={bagVolumes}
        onBagChange={handleBagChange}
        therapyMode={therapyMode}
        currentTargetId={currentTargetId}
        onReverseLines={() => handleAction('reverse_lines_button')}
      />
      <InfoModal content={infoModalContent} onClose={() => setInfoModalContent(null)} />
      <ModeSelectionModal
        isOpen={isModeModalOpen}
        onClose={() => setIsModeModalOpen(false)}
        currentMode={therapyMode}
        onSelectMode={handleModeChange}
      />
      <FeedbackModal 
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        db={firebase.db}
        isAuthReady={isAuthReady}
        userId={userId}
        sessionId={sessionId}
      />
      <div className="w-full max-w-[1400px] mx-auto flex flex-col rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden bg-slate-900 text-white font-sans">
      
      <div className="bg-slate-800/50 rounded-xl overflow-hidden flex flex-col">
        <Header 
            therapyMode={therapyMode} 
            onModeClick={() => setIsModeModalOpen(true)}
            onGiveFeedback={() => setIsFeedbackModalOpen(true)}
            mode={mode}
            onModeToggle={handleModeToggle}
        />
        
        <div className="p-6 flex flex-col">
            {mode === 'training' && controlsVisible && (
                <div className="bg-slate-900 border border-slate-700 p-6 rounded-lg shadow-inner mb-4 flex-shrink-0">
                    <h3 className="text-2xl font-bold text-center mb-4 text-slate-100 tracking-wide">Simulator Controls</h3>
                    
                    {activeTrainingGroup === null ? (
                        // Main Group View
                        <div className="flex flex-col items-center gap-4">
                             <ScenarioButton 
                                scenarioKey="normal"
                                scenario={scenarios['normal']}
                                currentScenario={currentScenario}
                                onClick={handleScenarioChange}
                                therapyMode={therapyMode}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-3xl">
                                {Object.keys(alarmGroupsConfig).map(groupKey => {
                                    const group = alarmGroupsConfig[groupKey];
                                    return (
                                        <button
                                            key={groupKey}
                                            onClick={() => setActiveTrainingGroup(groupKey)}
                                            className="group flex items-center justify-between p-4 bg-gradient-to-br from-sky-800 to-slate-800 border border-slate-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-sky-500/20 hover:border-sky-600 hover:-translate-y-px"
                                        >
                                            <span className="font-semibold text-lg text-sky-100">{group.name}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400 transition-colors group-hover:text-sky-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    ) : (
                        // Specific Group View
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="text-xl font-semibold text-sky-400">{alarmGroupsConfig[activeTrainingGroup].name}</h4>
                                <button 
                                    onClick={() => setActiveTrainingGroup(null)}
                                    className="px-4 py-2 rounded-lg font-medium text-slate-200 bg-slate-700 border border-slate-600 hover:bg-slate-600 hover:border-slate-400 transition-all duration-200 shadow-md hover:scale-105"
                                >
                                    &larr; Back to Groups
                                </button>
                            </div>
                            <div className="flex justify-center flex-wrap gap-3">
                                {alarmGroupsConfig[activeTrainingGroup].keys.map(scenarioKey => {
                                    // scenarioKey can be a single scenario (e.g., 'blood_leak') or a category (e.g., 'high_tmp')
                                    const scenario = scenarios[scenarioKey];
                                    const category = scenarioCategories[scenarioKey];
                                    const buttonData = scenario || category; // Get data from wherever it exists
                                    
                                    if (!buttonData) return null; // Safety check

                                    return <ScenarioButton 
                                                key={scenarioKey}
                                                scenarioKey={scenarioKey} // Pass the key itself
                                                scenario={buttonData} // Pass the scenario or category data
                                                currentScenario={currentScenario}
                                                onClick={handleScenarioChange}
                                                therapyMode={therapyMode}
                                           />
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {mode === 'training' && !controlsVisible && (
                 <div className="bg-slate-800 p-3 mb-4 rounded-lg shadow-md border border-slate-700 flex-shrink-0">
                    <div className="flex justify-center">
                        <button 
                            onClick={() => {
                                setControlsVisible(true);
                                setActiveTrainingGroup(null);
                                handleScenarioChange('normal'); // Go to normal ops when controls are shown
                            }}
                            className="bg-gradient-to-br from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                        >
                            Show Controls
                        </button>
                    </div>
                </div>
            )}

            {mode === 'practice' && (
                 <div className="bg-slate-800 p-3 mb-4 rounded-lg shadow-md border border-slate-700 flex-shrink-0">
                    <div className="flex justify-center">
                        <button 
                            onClick={handlePracticeScenarioChange}
                            className="bg-gradient-to-br from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white font-bold py-2 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                        >
                            New Random Scenario
                        </button>
                    </div>
                </div>
            )}

            {currentStepText && mode === 'training' && (
                <div className="bg-sky-900/50 border-2 border-sky-600/70 text-center p-3 mb-4 rounded-lg shadow-lg flex-shrink-0">
                    <p className="font-semibold text-xl tracking-wide">Next Step: <span className="text-amber-300 font-bold">{currentStepText}</span></p>
                </div>
            )}
            
            {currentScenario?.initialState?.troubleshooting && (() => {
                const tier = currentScenario.tier || 'warning';
                const tierStyle = {
                    warning:  { box: 'bg-red-800/80 border-red-500',      label: 'WARNING',  sub: 'Patient hazard — all pumps stopped' },
                    caution:  { box: 'bg-amber-800/70 border-amber-500',  label: 'CAUTION',  sub: 'Fluid pumps stopped — blood flow continues' },
                    advisory: { box: 'bg-yellow-800/60 border-yellow-600',label: 'ADVISORY', sub: 'Therapy continues' },
                }[tier];
                return (
                    <div className="mb-4 flex-shrink-0 space-y-2">
                        <div className={`${tierStyle.box} border-2 text-center p-3 rounded-lg shadow-lg animate-pulse flex justify-between items-center`}>
                            <div className="text-left">
                                <p className="text-xs font-semibold tracking-widest text-slate-200/80">{tierStyle.label} — {tierStyle.sub}</p>
                                <p className="font-bold text-2xl tracking-wide text-white">
                                    ALARM: {currentScenario.initialState.troubleshooting.problem}
                                </p>
                            </div>
                            {troubleshootingStep === 0 && (
                                <button
                                    id="acknowledge_button"
                                    onClick={() => handleAction('acknowledge_button')}
                                    className={`px-4 py-2 rounded-lg font-semibold text-black bg-amber-400 hover:bg-amber-300 transition-colors ${currentTargetId === 'acknowledge_button' ? 'glow-effect-box' : ''}`}
                                >
                                    Acknowledge
                                </button>
                            )}
                        </div>

                        {!isPumpRunning && (
                            <div className="pump-stopped-banner border-2 rounded-lg px-4 py-2.5 flex justify-between items-center">
                                <span className="font-bold text-xl text-amber-100">
                                    ⚠ BLOOD PUMP STOPPED{clampClosed ? ' — RETURN LINE CLAMPED' : ''}
                                </span>
                                <span className="text-sm text-amber-200/80">Pressures held at their alarm values</span>
                            </div>
                        )}
                        {isPumpRunning && !fluidPumpsRunning && (
                            <div className="border-2 border-amber-600/70 bg-amber-900/30 rounded-lg px-4 py-2.5 flex justify-between items-center">
                                <span className="font-bold text-xl text-amber-100">FLUID DELIVERY STOPPED</span>
                                <span className="text-sm text-amber-200/80">Blood and syringe pumps still running</span>
                            </div>
                        )}
                    </div>
                );
            })()}

            {actionFeedback && currentScenario?.initialState?.troubleshooting && (
                <div className={`mb-4 flex-shrink-0 rounded-lg border-l-4 px-4 py-3 shadow-lg flex items-start gap-3 ${
                    actionFeedback.tone === 'found'
                        ? 'bg-sky-900/60 border-sky-400'
                        : 'bg-slate-800/80 border-slate-500'
                }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 flex-shrink-0 mt-0.5 ${actionFeedback.tone === 'found' ? 'text-sky-300' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <div>
                        <p className="text-xs font-semibold tracking-wider text-slate-400">YOU FIND</p>
                        <p className={`text-lg ${actionFeedback.tone === 'found' ? 'text-sky-100' : 'text-slate-300'}`}>
                            {actionFeedback.text}
                        </p>
                    </div>
                </div>
            )}

            <div className="relative h-[500px] mb-4 flex-shrink-0">
                {/* SVG for fluid lines and animations */}
                <svg width="100%" height="100%" className="absolute top-0 left-0">
                    {/* Main fluid lines */}
                    <path d="M 70 190 L 198 190 L 198 305 L 320 190 L 490 190" stroke="#ef4444" strokeWidth="8" fill="none" />
                    <path d="M 620 190 L 988 190 L 988 305" stroke="#3b82f6" strokeWidth="8" fill="none" />
                    <path d="M 540 210 L 540 360" stroke="#a3e635" strokeWidth="10" fill="none" />

                    {/* Connector lines */}
                    {therapyMode !== 'CVVH' && <path d="M 610 170 L 610 300 L 680 300" stroke="#10b981" strokeWidth="6" fill="none" />}
                    {therapyMode !== 'CVVHD' && <path d="M 890 100 L 890 190" stroke="#a855f7" strokeWidth="6" fill="none" />}

                    {/* Return line clamp — closes on any Warning-tier alarm */}
                    <rect x="972" y="215" width="32" height="14" rx="3" fill={clampClosed ? '#f59e0b' : '#475569'} />

                    {/* Animated Particles */}
                    {isPumpRunning && (
                        <>
                            <circle className="particle-red" r="5" fill="#f87171" />
                            <circle className="particle-red" r="5" fill="#f87171" style={{ animationDelay: '1s' }} />
                            <circle className="particle-red" r="5" fill="#f87171" style={{ animationDelay: '2s' }} />
                            <circle className="particle-red" r="5" fill="#f87171" style={{ animationDelay: '3s' }} />
                            <circle className="particle-blue" r="5" fill="#60a5fa" />
                            <circle className="particle-blue" r="5" fill="#60a5fa" style={{ animationDelay: '1.5s' }} />

                        </>
                    )}
                </svg>

                {/* Clickable Overlays */}
                <div onClick={() => handleAction('access-line')} className={`absolute top-[186px] left-[70px] w-[130px] h-3 cursor-pointer transition-colors hover:bg-sky-400/30 ${currentTargetId === 'access-line' ? 'glow-effect-box bg-sky-400/30' : ''}`} title="Access Line"></div>
                <div onClick={() => handleAction('filter')} className={`absolute top-[170px] left-[490px] w-28 h-10 rounded-full border-2 border-slate-400 cursor-pointer transition-colors hover:bg-sky-400/30 ${currentTargetId === 'filter' ? 'glow-effect-box bg-sky-400/30' : ''}`} title="Filter"></div>
                <div onClick={() => handleAction('return-line')} className={`absolute top-[186px] left-[618px] w-[370px] h-3 cursor-pointer transition-colors hover:bg-sky-400/30 ${currentTargetId === 'return-line' ? 'glow-effect-box bg-sky-400/30' : ''}`} title="Return Line"></div>
                <div onClick={() => handleAction('effluent-line')} className={`absolute top-[205px] left-[535px] w-4 h-[150px] cursor-pointer z-20 transition-colors hover:bg-sky-400/30 ${currentTargetId === 'effluent-line' ? 'glow-effect-box bg-sky-400/30' : ''}`} title="Effluent Line"></div>

                {/* UI Components Placed over the SVG */}
                <div onClick={() => handleAction('blood-pump')} className={`absolute top-[80px] left-[150px] w-20 h-20 rounded-full -translate-x-1/2 -translate-y-1/2 border-4 flex flex-col items-center justify-center cursor-pointer z-20 transition-all duration-300 hover:scale-110 ${isPumpRunning ? 'bg-gradient-to-br from-green-500 to-green-700 border-green-300 shadow-lg shadow-green-500/30' : 'bg-gradient-to-br from-amber-500 to-amber-700 border-amber-300 shadow-lg shadow-amber-500/40 animate-pulse'} ${currentTargetId === 'blood-pump' ? 'glow-effect' : ''}`} title="Blood Pump">
                    <span className="text-white font-extrabold text-sm leading-tight">{isPumpRunning ? 'RUNNING' : 'STOPPED'}</span>
                    <span className="text-xs text-white/80">Pump</span>
                </div>
                
                {/* De-aeration Chamber Visual */}
                <div className="absolute top-[40px] left-[1000px] w-12 h-24 bg-slate-700/50 border-2 border-slate-500 rounded-lg flex flex-col justify-end">
                    <div className="h-3/4 bg-blue-500/50 relative">
                        {/* Foam bubbles */}
                        <div className="absolute top-1 left-2 w-2 h-2 bg-white/80 rounded-full"></div>
                        <div className="absolute top-2 left-5 w-1 h-1 bg-white/80 rounded-full"></div>
                        <div className="absolute top-3 left-1 w-3 h-3 bg-white/80 rounded-full"></div>
                        <div className="absolute top-1 left-8 w-2 h-2 bg-white/80 rounded-full"></div>
                    </div>
                </div>

                {/* De-aeration Chamber Clickable Area & Label */}
                <div onClick={() => handleAction('deaeration_chamber')} className={`absolute top-[40px] left-[1000px] w-12 h-24 rounded-lg cursor-pointer transition-colors hover:bg-sky-400/30 ${currentTargetId === 'deaeration_chamber' ? 'glow-effect-box bg-sky-400/30' : ''}`} title="De-aeration Chamber"></div>
                <div className="absolute top-[15px] left-[850px] text-sm font-semibold text-gray-200 bg-slate-900/50 px-2 py-1 rounded">De-aeration Chamber</div>


                {/* Labels */}
                <div className="absolute top-[155px] left-[40px] text-sm font-semibold text-red-300 bg-slate-900/50 px-2 py-1 rounded">Access Line (From Patient)</div>
                <div className="absolute top-[140px] left-[480px] text-sm font-semibold text-gray-200 bg-slate-900/50 px-2 py-1 rounded">Hemo-Filter</div>
                <div className="absolute top-[155px] right-[15px] text-sm font-semibold text-blue-300 bg-slate-900/50 px-2 py-1 rounded">Return Line (To Patient)</div>
                <div className="absolute top-[370px] left-[500px] text-sm font-semibold text-lime-300 bg-slate-900/50 px-2 py-1 rounded">Effluent Line</div>

                {/* Component Displays */}
                {therapyMode !== 'CVVHD' && <div className="absolute top-[280px] left-0"><ParameterDisplay paramKey="pbp" currentTargetId={currentTargetId} onClick={() => setInfoModalContent(componentInfo.pbp)} label="PBP" value={params.pbp} unit="mL/h" color="pink" onAdjust={(amt) => handleAdjust('pbp', amt)} bagType="pbp" bagVolume={bagVolumes.pbp} /></div>}
                <div className="absolute top-[150px] left-[220px]"><ParameterDisplay paramKey="bfr" currentTargetId={currentTargetId} onClick={() => setInfoModalContent(componentInfo.bfr)} label="BFR" value={params.bfr} unit="mL/min" color="red" onAdjust={(amt) => handleAdjust('bfr', amt)} /></div>
                <div className="absolute top-[150px] left-[390px]"><ParameterDisplay paramKey="syringe" currentTargetId={currentTargetId} onClick={() => setInfoModalContent(componentInfo.syringe)} label="Syringe" value={params.syringe} unit="mL/h" color="blue" onAdjust={(amt) => handleAdjust('syringe', amt)} /></div>
                <div className="absolute top-[400px] left-[400px]"><ParameterDisplay paramKey="pfr" currentTargetId={currentTargetId} onClick={() => setInfoModalContent(componentInfo.pfr)} label="PFR" value={params.pfr} unit="mL/h" color="cyan" onAdjust={(amt) => handleAdjust('pfr', amt)} /></div>
                {therapyMode !== 'CVVH' && <div className="absolute top-[280px] left-[680px]"><ParameterDisplay paramKey="dia" currentTargetId={currentTargetId} onClick={() => setInfoModalContent(componentInfo.dialysate)} label="Dialysate" value={params.dia} unit="mL/h" color="green" onAdjust={(amt) => handleAdjust('dia', amt)} bagType="dia" bagVolume={bagVolumes.dia} /></div>}
                {therapyMode !== 'CVVHD' && <div className="absolute top-[280px] left-[890px]"><ParameterDisplay paramKey="rep" currentTargetId={currentTargetId} onClick={() => setInfoModalContent(componentInfo.replacement)} label="Replacement" value={params.rep} unit="mL/h" color="purple" onAdjust={(amt) => handleAdjust('rep', amt)} bagType="rep" bagVolume={bagVolumes.rep} /></div>}
                <div className="absolute top-[60px] left-[780px] flex items-center space-x-2 bg-slate-700/80 p-2 rounded-lg shadow-lg border border-slate-600"><span className="text-sky-300 font-semibold text-lg">Heater</span><div className="bg-slate-800 px-3 py-1 rounded"><span className="text-2xl font-bold font-mono text-emerald-400">36.5</span><span className="text-base text-slate-400"> °C</span></div></div>
            </div>

            <div className="grid grid-cols-12 gap-6 mt-4 flex-shrink-0">
              <div className="col-span-4 flex flex-col">
                <div className="flex justify-around bg-slate-800/70 p-4 rounded-xl shadow-inner border border-slate-700/50">
                  <PressureGauge onClick={() => setInfoModalContent(componentInfo.access)} label="Access" value={pressures.access} min={-250} max={50} unit="mmHg" />
                  <PressureGauge onClick={() => setInfoModalContent(componentInfo.return)} label="Return" value={pressures.return} min={0} max={300} unit="mmHg" />
                  <PressureGauge onClick={() => setInfoModalContent(componentInfo.tmp)} label="TMP" value={pressures.tmp} min={0} max={350} unit="mmHg" />
                  <PressureGauge onClick={() => setInfoModalContent(componentInfo.deltaP)} label="ΔP" value={pressures.deltaP} min={0} max={400} unit="mmHg" />
                </div>
              </div>
              <div className="col-span-4 bg-slate-800/70 p-4 rounded-xl shadow-inner border border-slate-700/50">
                  <div className="grid grid-cols-2 gap-4 h-full">
                      <div className="bg-slate-900 rounded-lg p-3 cursor-pointer shadow-md flex flex-col justify-center items-center text-center transition-all duration-200 hover:bg-slate-800/50 hover:scale-105" onClick={() => setInfoModalContent(componentInfo.filter)}>
                          <h3 className="text-xl font-bold text-sky-300">Filter Pressure</h3>
                          <p className="text-4xl font-bold font-mono text-amber-400 mt-1">{pressures.tmp}<span className="text-xl text-slate-400 ml-2">mmHg</span></p>
                      </div>
                      <div className="bg-slate-900 rounded-lg p-3 cursor-pointer shadow-md flex flex-col justify-center items-center text-center transition-all duration-200 hover:bg-slate-800/50 hover:scale-105" onClick={() => setInfoModalContent(componentInfo.effluent)}>
                          <h3 className="text-xl font-bold text-sky-300">Effluent Dose</h3>
                          <p className="text-4xl font-bold font-mono text-emerald-400 mt-1">{effluentDose.toFixed(1)}<span className="text-lg text-slate-400 ml-2">mL/kg/h</span></p>
                      </div>
                  </div>
              </div>
              <div className="col-span-4 bg-slate-800/70 rounded-xl p-4 h-full flex flex-col justify-center shadow-lg border border-slate-700/50 space-y-4">
                  <button
                      id="key_continue"
                      onClick={() => handleAction('key_continue')}
                      title="Clears the alarm and restarts any stopped pumps. If the problem has not been fixed, the alarm returns."
                      className={`w-full bg-gradient-to-br from-emerald-600 to-emerald-800 hover:from-emerald-500 hover:to-emerald-700 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 shadow-lg text-lg ${currentTargetId === 'key_continue' ? 'glow-effect-box' : ''}`}
                  >
                    Resume Therapy
                  </button>

                  <button 
                      onClick={() => setIsFluidsModalOpen(true)} 
                      className={`w-full bg-gradient-to-br from-sky-600 to-sky-800 hover:from-sky-700 hover:to-sky-900 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 shadow-lg text-lg ${currentTargetId && (currentTargetId.startsWith('change_')) ? 'glow-effect-box' : ''}`}
                  >
                    Manage Fluids
                  </button>
                  
                  <button
                      id="reverse_lines_button"
                      onClick={() => handleAction('reverse_lines_button')}
                      className={`w-full bg-gradient-to-br from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 shadow-lg text-lg ${currentTargetId === 'reverse_lines_button' ? 'glow-effect-box' : ''}`}
                  >
                      Reverse Access/Return Lines
                  </button>

                  <button
                      id="key_change_set"
                      onClick={() => handleAction('key_change_set')}
                      title="Ends the treatment and loads a new circuit."
                      className={`w-full bg-gradient-to-br from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white font-bold py-3 px-4 rounded-lg transition-all duration-200 shadow-lg text-lg ${currentTargetId === 'key_change_set' ? 'glow-effect-box' : ''}`}
                  >
                      Change Set
                  </button>
              </div>
            </div>
        </div>
      </div>
      
      <p className="text-center text-xs text-slate-500 py-4 flex-shrink-0">
        Created by Dr. Amrit Kirpalani MD, MEHP, FRCPC. For educational purposes only. Not intended for clinical decision-making.
      </p>
    </div>
    </div>
  );
}